import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Calendar1, ListFilter, RefreshCcw, Gauge, Download, FileText, FileSpreadsheet, ChevronDown, Search, CheckCircle, X, Info } from 'lucide-react';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { sendSeraAtendidaAmanha, sendSeraAtendidaHoje } from '../../process-config/services/emailNotificationsService';
import FutureDateModalOficina from './FutureDateModalOficina';
import { formatNumber } from '../../new-os/utils/formatters';
import './OficinaView.css';

function formatPecaContador(val) {
  if (val == null || val === '') return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  return formatNumber(n);
}

const OFICINA_DRAG_MIME = 'application/oficina-os-drag';

function parseOficinaDragPayload(raw) {
  if (!raw || !String(raw).trim()) return null;
  try {
    const j = JSON.parse(raw);
    if (j?.numeroOS && (j.from === 'PENDENTE' || j.from === 'PARA_AMANHA')) {
      return { numeroOS: String(j.numeroOS).trim(), from: j.from };
    }
  } catch (_) {
    /* legado: só o número da OS = Pendente */
  }
  const id = String(raw).trim();
  return id ? { numeroOS: id, from: 'PENDENTE' } : null;
}

function AlterarStatusSelectOficina({ value, onChange, options, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('click', onOutside);
    return () => document.removeEventListener('click', onOutside);
  }, [open]);
  const valueStr = value != null ? String(value) : '';
  const selected = options.find((o) => String(o.codigo) === valueStr);
  const label = selected ? (selected.nome || selected.codigo) : 'Selecione...';
  const handleOptionSelect = (opt, e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(opt.codigo != null ? String(opt.codigo) : '');
    setOpen(false);
  };
  return (
    <div className="oficina-alterar-status-select-wrap" ref={ref}>
      <button type="button" className="oficina-alterar-status-select-trigger" onClick={() => !disabled && setOpen((o) => !o)} disabled={disabled}>
        <span>{label}</span>
        <span className="oficina-alterar-status-select-chevron" aria-hidden style={{ transform: open ? 'rotate(180deg)' : 'none' }}>▼</span>
      </button>
      {open && (
        <div className="oficina-alterar-status-select-dropdown">
          {options.map((opt) => (
            <div key={String(opt.codigo)} className="oficina-alterar-status-select-option" data-selected={String(opt.codigo) === valueStr} onMouseDown={(e) => handleOptionSelect(opt, e)} role="option" aria-selected={String(opt.codigo) === valueStr}>
              {opt.nome || opt.codigo}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const OficinaView = ({ apiBaseUrl = '', onDataSourceReady }) => {
  const base = apiBaseUrl || '';
  const [activeFilter, setActiveFilter] = useState('para-hoje');
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedOS, setSelectedOS] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showClosureForm, setShowClosureForm] = useState(false);
  const [closureForm, setClosureForm] = useState({
    dataTermino: '',
    horaTermino: '',
    condicaoFinal: '',
    codigoCondicao: '',
    contadores: {
      a4pb: '',
      a4cor: '',
      dig: '',
      a3pb: '',
      a3cor: ''
    },
    laudoTecnico: ''
  });
  const [condicaoSelectOpen, setCondicaoSelectOpen] = useState(false);
  const condicaoSelectRef = useRef(null);
  const [condicoesOptions, setCondicoesOptions] = useState([]);
  const [isLoadingCondicoes, setIsLoadingCondicoes] = useState(false);
  const [showA3PB, setShowA3PB] = useState(false);
  const [showA3Cor, setShowA3Cor] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [activeHistoryTab, setActiveHistoryTab] = useState('movimentacao');
  const [expandedMotivo, setExpandedMotivo] = useState(null);
  const [expandedLaudo, setExpandedLaudo] = useState(null);
  const [showAberturaFilter, setShowAberturaFilter] = useState(false);
  const [showFechamentoFilter, setShowFechamentoFilter] = useState(false);
  const [aberturaStart, setAberturaStart] = useState('');
  const [aberturaEnd, setAberturaEnd] = useState('');
  const [fechamentoStart, setFechamentoStart] = useState('');
  const [fechamentoEnd, setFechamentoEnd] = useState('');
  const [showClienteFilter, setShowClienteFilter] = useState(false);
  const [showTipoFilter, setShowTipoFilter] = useState(false);
  const [selectedClientes, setSelectedClientes] = useState([]);
  const [selectedTipos, setSelectedTipos] = useState([]);
  const [clienteSearchText, setClienteSearchText] = useState('');
  const [tipoSearchText, setTipoSearchText] = useState('');
  const [showTecnicoFilter, setShowTecnicoFilter] = useState(false);
  const [selectedTecnicos, setSelectedTecnicos] = useState([]);
  const [tecnicoSearchText, setTecnicoSearchText] = useState('');
  // Estados para aba Peças trocadas
  const [showPecasDataFilter, setShowPecasDataFilter] = useState(false);
  const [pecasDataStart, setPecasDataStart] = useState('');
  const [pecasDataEnd, setPecasDataEnd] = useState('');
  const [showReferenciaFilter, setShowReferenciaFilter] = useState(false);
  const [selectedReferencias, setSelectedReferencias] = useState([]);
  const [referenciaSearchText, setReferenciaSearchText] = useState('');
  const [showProdutoFilter, setShowProdutoFilter] = useState(false);
  const [selectedProdutos, setSelectedProdutos] = useState([]);
  const [produtoSearchText, setProdutoSearchText] = useState('');
  const [showAlterarStatusModal, setShowAlterarStatusModal] = useState(false);
  const [opcoesStatusConclusao, setOpcoesStatusConclusao] = useState([]);
  const [selectedCodStatusConclusao, setSelectedCodStatusConclusao] = useState('');
  const [isOpcoesStatusLoading, setIsOpcoesStatusLoading] = useState(false);
  const [alterarStatusLoading, setAlterarStatusLoading] = useState(false);
  const [alterarStatusFeedback, setAlterarStatusFeedback] = useState(null);
  
  // Estados para dados do backend
  const [filterCounts, setFilterCounts] = useState({
    'para-hoje': 0,
    'para-amanha': 0,
    'proximos-dias': 0
  });
  const [impressoras, setImpressoras] = useState([]);
  const [pausados, setPausados] = useState([]);
  const [pendentes, setPendentes] = useState([]);
  const [paraAmanha, setParaAmanha] = useState([]);
  const [proximosDias, setProximosDias] = useState({ comPrevisao: [], semPrevisao: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [ordensServico, setOrdensServico] = useState([]);
  const [isLoadingOrdens, setIsLoadingOrdens] = useState(false);
  const [osDetalhes, setOsDetalhes] = useState({ defeito: null, observacoes: null });
  const [showContadoresModal, setShowContadoresModal] = useState(false);
  const [isLoadingContadores, setIsLoadingContadores] = useState(false);
  const [contadores, setContadores] = useState({
    a4pb: null,
    a4cor: null,
    dig: null,
    a3pb: null,
    a3cor: null
  });
  const [ultimoAtendimento, setUltimoAtendimento] = useState(null);
  const [isLoadingUltimoAtendimento, setIsLoadingUltimoAtendimento] = useState(false);
  const [movimentacaoData, setMovimentacaoData] = useState([]);
  const [isLoadingMovimentacao, setIsLoadingMovimentacao] = useState(false);
  const [ordensHistoricoData, setOrdensHistoricoData] = useState([]);
  const [isLoadingOrdensHistorico, setIsLoadingOrdensHistorico] = useState(false);
  const [pecasTrocadasData, setPecasTrocadasData] = useState([]);
  const [isLoadingPecasTrocadas, setIsLoadingPecasTrocadas] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);
  const [searchType, setSearchType] = useState('Buscar por...');
  const [searchValue, setSearchValue] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchDropdownRef = useRef(null);
  const [foundOS, setFoundOS] = useState(null);
  const [isSearchingOS, setIsSearchingOS] = useState(false);
  const [highlightedOS, setHighlightedOS] = useState(null);
  const [userCodTec, setUserCodTec] = useState('');
  /** @type {null | { numeroOS: string, from: 'PENDENTE' | 'PARA_AMANHA' }} */
  const [dragOficinaCtx, setDragOficinaCtx] = useState(null);
  /** Número da OS em arraste; alias do ctx (evita ReferenceError se algum trecho ainda usar o nome antigo). */
  const dragOsPendente = dragOficinaCtx?.numeroOS ?? null;
  /** @type {null | 'hoje' | 'amanha' | 'proximos'} */
  const [dropHoverTarget, setDropHoverTarget] = useState(null);
  const [showFuturaModal, setShowFuturaModal] = useState(false);
  const [futuraModalData, setFuturaModalData] = useState(null);
  const [oficinaMoveLoading, setOficinaMoveLoading] = useState(false);
  
  // Função para buscar dados da API
  const fetchOficinaData = async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true);
      const token = localStorage.getItem('authToken');
      
      const resumoResponse = await fetch(`${base}/api/oficina/resumo`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (resumoResponse.ok) {
        const contentType = resumoResponse.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          await resumoResponse.text();
          throw new Error('Resposta não é JSON');
        }
        
        const resumoData = await resumoResponse.json();
        if (resumoData.success) {
          const d = resumoData.data || {};
          setUserCodTec(d.codTec ? String(d.codTec).trim() : '');
          setFilterCounts({
            'para-hoje': d['para-hoje'] ?? 0,
            'para-amanha': d['para-amanha'] ?? 0,
            'proximos-dias': d['proximos-dias'] ?? 0,
          });
          onDataSourceReady?.('sql_server');
        } else {
          if (resumoData.message && resumoData.message.includes('Código de técnico')) {
            setNotification({
              message: resumoData.message,
              type: 'error'
            });
          }
        }
      } else {
        const contentType = resumoResponse.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          await resumoResponse.json();
        } else {
          await resumoResponse.text();
        }
      }
      
      const cardsResponse = await fetch(`${base}/api/oficina/cards?filter=${activeFilter}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (cardsResponse.ok) {
        const contentType = cardsResponse.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          await cardsResponse.text();
          throw new Error('Resposta não é JSON');
        }
        
        const cardsData = await cardsResponse.json();
        if (cardsData.success) {
          setImpressoras(cardsData.data.emAndamento || []);
          setPausados(cardsData.data.pausado || []);
          setPendentes(cardsData.data.pendente || []);
          setParaAmanha(cardsData.data.paraAmanha || []);
          setProximosDias(cardsData.data.proximosDias || { comPrevisao: [], semPrevisao: [] });
        }
      } else {
        const contentType = cardsResponse.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          await cardsResponse.json();
        } else {
          await cardsResponse.text();
        }
      }
    } catch (error) {
      // Erro tratado via notificação/estado
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };
  
  // Pausar revisão (EM ANDAMENTO)
  const handlePauseReview = async () => {
    if (!selectedOS?.numeroOS) {
      setNotification({
        message: 'Nenhuma ordem de serviço selecionada',
        type: 'error'
      });
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${base}/api/oficina/pausar-revisao`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          numeroOS: selectedOS.numeroOS
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setNotification({
          message: data.message || `OS ${selectedOS.numeroOS} pausada com sucesso`,
          type: 'success'
        });
        
        // Fechar painel de detalhes
        setSelectedOS(null);
        
        // Atualizar dados (refresh)
        await handleRefresh();
        
        setTimeout(() => {
          setNotification(null);
        }, 3000);
      } else {
        throw new Error(data.message || 'Erro ao pausar revisão');
      }
    } catch (error) {
      console.error('Erro ao pausar revisão:', error);
      setNotification({
        message: error.message || 'Erro ao pausar revisão',
        type: 'error'
      });
      setTimeout(() => {
        setNotification(null);
      }, 3000);
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Continuar revisão (PAUSADO) – mesma ação de iniciar revisão
  const handleContinueReview = async () => {
    await startRevisao();
  };
  
  // Função para atualizar dados manualmente
  const handleRefresh = async () => {
    // Fechar painel de detalhes da OS se estiver aberto
    setSelectedOS(null);
    setShowClosureForm(false);
    setSelectedEquipment(null);
    setOrdensServico([]);
    
    setIsLoading(true);
    await fetchOficinaData(true);
  };
  
  // Buscar dados ao montar componente e quando filtro muda
  useEffect(() => {
    fetchOficinaData();
    // Limpar seleção de equipamento e ordens ao mudar de aba, exceto se houver OS encontrada
    if (!foundOS) {
      setSelectedEquipment(null);
      setSelectedOS(null);
      setShowClosureForm(false);
      setOrdensServico([]);
      setOsDetalhes({ defeito: null, observacoes: null });
    } else {
      // Se há OS encontrada, aguardar dados carregarem e selecionar novamente
      setTimeout(() => {
        if (foundOS && foundOS.filtro === activeFilter) {
          selecionarOSEncontrada(foundOS);
        }
      }, 1000);
    }
  }, [activeFilter]);

  // Buscar defeito e observações quando uma OS é selecionada
  useEffect(() => {
    const fetchDefeitoObservacoes = async () => {
      if (!selectedOS?.numeroOS) {
        setOsDetalhes({ defeito: null, observacoes: null });
        return;
      }

      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${base}/api/oficina/os/${selectedOS.numeroOS}/defeito-observacoes`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setOsDetalhes({
              defeito: data.data.defeito || selectedOS.problema || '-',
              observacoes: data.data.observacoes || '-'
            });
          } else {
            setOsDetalhes({
              defeito: selectedOS.problema || '-',
              observacoes: '-'
            });
          }
        } else {
          setOsDetalhes({
            defeito: selectedOS.problema || '-',
            observacoes: '-'
          });
        }
      } catch (error) {
        console.error('Erro ao buscar defeito e observações:', error);
        setOsDetalhes({
          defeito: selectedOS.problema || '-',
          observacoes: '-'
        });
      }
    };

    fetchDefeitoObservacoes();
  }, [selectedOS]);

  // Resetar contadores quando OS muda
  useEffect(() => {
    setShowContadoresModal(false);
    setContadores({
      a4pb: null,
      a4cor: null,
      dig: null,
      a3pb: null,
      a3cor: null
    });
    setUltimoAtendimento(null);
  }, [selectedOS]);

  // Buscar último atendimento quando uma OS é selecionada
  useEffect(() => {
    const fetchUltimoAtendimento = async () => {
      if (!selectedOS?.numeroSerie) {
        setUltimoAtendimento(null);
        return;
      }

      setIsLoadingUltimoAtendimento(true);
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${base}/api/oficina/ultimo-atendimento?serie=${encodeURIComponent(selectedOS.numeroSerie)}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setUltimoAtendimento(data.data);
          } else {
            setUltimoAtendimento(null);
          }
        } else {
          setUltimoAtendimento(null);
        }
      } catch (error) {
        console.error('Erro ao buscar último atendimento:', error);
        setUltimoAtendimento(null);
      } finally {
        setIsLoadingUltimoAtendimento(false);
      }
    };

    fetchUltimoAtendimento();
  }, [selectedOS]);

  // Função para buscar contadores
  const fetchContadores = async () => {
    if (!selectedOS?.numeroSerie) return;
    
    setIsLoadingContadores(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${base}/api/oficina/contadores?serie=${encodeURIComponent(selectedOS.numeroSerie)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setContadores({
            a4pb: data.data.ContadorPB || 0,
            a4cor: data.data.ContadorCor || 0,
            dig: data.data.ContadorDig || 0,
            a3pb: data.data.ContadorA3PB || 0,
            a3cor: data.data.ContadorA3Cor || 0
          });
        }
      }
    } catch (error) {
      console.error('Erro ao buscar contadores:', error);
    } finally {
      setIsLoadingContadores(false);
    }
  };

  const openContadoresModal = () => {
    setShowContadoresModal(true);
    fetchContadores();
  };

  // Função para buscar ordens de serviço do equipamento
  const fetchOrdensByEquipamento = async (modelo, statusCard, dataPrevisao = null) => {
    try {
      setIsLoadingOrdens(true);
      const token = localStorage.getItem('authToken');
      
      // Mapear status baseado no filtro ativo
      let statusToSend = statusCard;
      if (activeFilter === 'para-hoje' && statusCard === 'EM ANDAMENTO') {
        statusToSend = 'EM ANDAMENTO';
      } else if (activeFilter === 'para-hoje' && statusCard === 'PENDENTE') {
        statusToSend = 'PENDENTE';
      } else if (activeFilter === 'para-amanha') {
        statusToSend = 'PARA AMANHA';
      } else if (activeFilter === 'proximos-dias') {
        statusToSend = 'PROXIMOS_DIAS';
      }
      
      let url = `${base}/api/oficina/ordens?modelo=${encodeURIComponent(modelo)}&status=${encodeURIComponent(statusToSend)}`;
      if (dataPrevisao) {
        url += `&dataPrevisao=${encodeURIComponent(dataPrevisao)}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setOrdensServico(data.data || []);
        } else {
          setOrdensServico([]);
        }
      } else {
        setOrdensServico([]);
      }
    } catch (error) {
      console.error('Erro ao buscar ordens:', error);
      setOrdensServico([]);
    } finally {
      setIsLoadingOrdens(false);
    }
  };
  
  // Função para selecionar equipamento e exibir ordens de serviço
  const handleEquipmentClick = async (modelo, quantidade, statusCard, dataPrevisao = null) => {
    // Fechar painel de detalhes da OS se estiver aberto
    setSelectedOS(null);
    setShowClosureForm(false);
    
    // Criar chave única combinando modelo + statusCard + dataPrevisao
    const equipmentKey = `${modelo}_${statusCard}_${dataPrevisao || ''}`;
    const currentKey = selectedEquipment ? `${selectedEquipment.modelo}_${selectedEquipment.statusCard}_${selectedEquipment.dataPrevisao || ''}` : null;
    
    if (currentKey === equipmentKey) {
      setSelectedEquipment(null);
      setOrdensServico([]);
    } else {
      setSelectedEquipment({ modelo, quantidade, statusCard, dataPrevisao });
      await fetchOrdensByEquipamento(modelo, statusCard, dataPrevisao);
    }
  };

  const canDragOficinaOs =
    !oficinaMoveLoading &&
    ((activeFilter === 'para-hoje' && selectedEquipment?.statusCard === 'PENDENTE') ||
      (activeFilter === 'para-amanha' && selectedEquipment?.statusCard === 'PARA AMANHA'));

  const handleOsOficinaDragStart = (e, numeroOS) => {
    if (!canDragOficinaOs) {
      e.preventDefault();
      return;
    }
    const id = String(numeroOS);
    const from =
      activeFilter === 'para-amanha' && selectedEquipment?.statusCard === 'PARA AMANHA'
        ? 'PARA_AMANHA'
        : 'PENDENTE';
    const payload = JSON.stringify({ numeroOS: id, from });
    e.dataTransfer.setData(OFICINA_DRAG_MIME, payload);
    e.dataTransfer.setData('application/oficina-os', id);
    e.dataTransfer.effectAllowed = 'move';
    setDragOficinaCtx({ numeroOS: id, from });
    try {
      e.currentTarget.classList.add('os-card--dragging');
    } catch (_) { /* ignore */ }
  };

  const handleOsOficinaDragEnd = (e) => {
    try {
      e.currentTarget.classList.remove('os-card--dragging');
    } catch (_) { /* ignore */ }
    setDragOficinaCtx(null);
    setDropHoverTarget(null);
  };

  const handleDropProximosTab = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropHoverTarget(null);
    const raw = e.dataTransfer.getData(OFICINA_DRAG_MIME) || e.dataTransfer.getData('application/oficina-os');
    const payload = parseOficinaDragPayload(raw);
    setDragOficinaCtx(null);
    if (!payload || (payload.from !== 'PENDENTE' && payload.from !== 'PARA_AMANHA')) return;
    const { numeroOS } = payload;
    if (!userCodTec) {
      setNotification({ type: 'error', message: 'Código do técnico indisponível. Atualize a página.', moveToast: true });
      setTimeout(() => setNotification(null), 5000);
      return;
    }
    setFuturaModalData({
      orderIds: [numeroOS],
      fromAmanha: payload.from === 'PARA_AMANHA',
    });
    setShowFuturaModal(true);
  };

  const handleFuturaModalConfirm = async (date, noDate, notes) => {
    const standbyNotes = String(notes || '').trim();
    if (noDate && standbyNotes.length < 10) {
      setNotification({ type: 'error', message: 'Observações: mínimo 10 caracteres.', moveToast: true });
      setTimeout(() => setNotification(null), 5000);
      return;
    }
    const modalPayload = futuraModalData;
    if (!modalPayload?.orderIds?.length) return;
    if (!userCodTec) {
      setNotification({ type: 'error', message: 'Código do técnico indisponível. Atualize a página.', moveToast: true });
      setTimeout(() => setNotification(null), 5000);
      return;
    }
    const orderIds = modalPayload.orderIds.map((id) => String(id).trim());
    const techId = String(userCodTec).padStart(4, '0');
    const modeloEq = selectedEquipment?.modelo;
    const dataPrevEq = selectedEquipment?.dataPrevisao || null;
    const eraPendente = selectedEquipment?.statusCard === 'PENDENTE';
    const eraParaAmanha = selectedEquipment?.statusCard === 'PARA AMANHA';
    const veioDeAmanha = !!modalPayload.fromAmanha;

    setShowFuturaModal(false);
    setFuturaModalData(null);

    const token = localStorage.getItem('authToken');
    let userId = null;
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        userId = JSON.parse(atob(tokenParts[1])).id;
      }
    } catch (_) { /* ignore */ }

    if (!userId) {
      setNotification({
        type: 'error',
        message: 'Sessão inválida. Faça login novamente.',
        moveToast: true,
      });
      setTimeout(() => setNotification(null), 6000);
      return;
    }

    setOficinaMoveLoading(true);
    try {
      if (veioDeAmanha) {
        await Promise.all(
          orderIds.map(async (orderId) => {
            try {
              const rm = await fetch(`${base}/api/route/remove-order-by-os`, {
                method: 'DELETE',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ orderNumber: orderId }),
              });
              await rm.json();
            } catch (_) {
              /* Board segue mesmo com aviso no servidor */
            }
          })
        );
      }

      if (noDate) {
        const response = await fetch(`${base}/api/orders/update-status`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            orderIds,
            targetSection: 'FUTURA',
            technicianId: techId,
          }),
        });
        const result = await response.json();
        const apiErrors = result.data?.errors || [];
        if (!response.ok || !result.success || apiErrors.length > 0) {
          const errMsg =
            apiErrors[0]?.error || result.message || 'Não foi possível mover para "Próximos dias" (sem previsão).';
          setNotification({ type: 'error', message: errMsg, moveToast: true });
          setTimeout(() => setNotification(null), 6000);
          return;
        }
        for (let i = 0; i < orderIds.length; i += 1) {
          const orderId = orderIds[i];
          const insertResponse = await fetch(`${base}/api/orders/add-standby`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              os: orderId,
              id_tech: techId,
              schedule: null,
              id_user: userId,
              notes: standbyNotes,
            }),
          });
          const insertResult = await insertResponse.json();
          if (!insertResult.success) {
            const errMsg = insertResult.message || `Erro ao gravar observações da OS ${orderId}.`;
            setNotification({ type: 'error', message: errMsg, moveToast: true });
            setTimeout(() => setNotification(null), 6000);
            return;
          }
        }
      } else {
        const [year, month, day] = date.split('-').map(Number);
        const scheduleISOString = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0)).toISOString();

        for (let i = 0; i < orderIds.length; i += 1) {
          const orderId = orderIds[i];
          const insertResponse = await fetch(`${base}/api/orders/add-standby`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              os: orderId,
              id_tech: techId,
              schedule: scheduleISOString,
              id_user: userId,
              notes: standbyNotes,
            }),
          });
          const insertResult = await insertResponse.json();
          if (!insertResult.success) {
            const errMsg = insertResult.message || `Erro ao gravar previsão da OS ${orderId}.`;
            setNotification({ type: 'error', message: errMsg, moveToast: true });
            setTimeout(() => setNotification(null), 6000);
            return;
          }
        }

        const response = await fetch(`${base}/api/orders/update-status`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            orderIds,
            targetSection: 'FUTURA',
            technicianId: techId,
          }),
        });
        const result = await response.json();
        const apiErrors = result.data?.errors || [];
        if (!response.ok || !result.success || apiErrors.length > 0) {
          const errMsg =
            apiErrors[0]?.error || result.message || 'Não foi possível atualizar status para "Próximos dias".';
          setNotification({ type: 'error', message: errMsg, moveToast: true });
          setTimeout(() => setNotification(null), 6000);
          return;
        }
      }

      setOrdensServico((prev) => prev.filter((o) => !orderIds.includes(String(o.numeroOS))));
      setSelectedOS((prev) => (prev && orderIds.includes(String(prev.numeroOS)) ? null : prev));
      await fetchOficinaData(false);
      if (eraPendente && modeloEq) {
        await fetchOrdensByEquipamento(modeloEq, 'PENDENTE', dataPrevEq);
      }
      if (eraParaAmanha && modeloEq) {
        await fetchOrdensByEquipamento(modeloEq, 'PARA AMANHA', dataPrevEq);
      }
      setNotification({
        type: 'success',
        message:
          orderIds.length === 1
            ? `OS ${orderIds[0]} movida para "Próximos dias".`
            : `${orderIds.length} OS movidas para "Próximos dias".`,
        moveToast: true,
      });
      setTimeout(() => setNotification(null), 4000);
    } catch (err) {
      setNotification({
        type: 'error',
        message: err.message || 'Erro ao processar previsão.',
        moveToast: true,
      });
      setTimeout(() => setNotification(null), 6000);
    } finally {
      setOficinaMoveLoading(false);
    }
  };

  const handleDropParaHojeFromAmanha = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropHoverTarget(null);
    const raw = e.dataTransfer.getData(OFICINA_DRAG_MIME) || e.dataTransfer.getData('application/oficina-os');
    const payload = parseOficinaDragPayload(raw);
    setDragOficinaCtx(null);
    if (!payload || payload.from !== 'PARA_AMANHA') return;
    const numeroOS = payload.numeroOS;
    if (!userCodTec) {
      setNotification({ type: 'error', message: 'Código do técnico indisponível. Atualize a página.', moveToast: true });
      setTimeout(() => setNotification(null), 5000);
      return;
    }
    const techId = String(userCodTec).padStart(4, '0');
    const modeloEq = selectedEquipment?.modelo;
    const dataPrevEq = selectedEquipment?.dataPrevisao || null;
    setOficinaMoveLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      try {
        const rm = await fetch(`${base}/api/route/remove-order-by-os`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ orderNumber: numeroOS }),
        });
        await rm.json();
      } catch (_) {
        /* Board segue mesmo com falha pontual no roadmap */
      }
      const response = await fetch(`${base}/api/orders/update-status`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderIds: [numeroOS],
          targetSection: 'Previsto para hoje',
          technicianId: techId,
        }),
      });
      const result = await response.json();
      const apiErrors = result.data?.errors || [];
      if (!response.ok || !result.success || apiErrors.length > 0) {
        const errMsg =
          apiErrors[0]?.error || result.message || 'Não foi possível mover a OS para "Para hoje" (Pendente).';
        setNotification({ type: 'error', message: errMsg, moveToast: true });
        setTimeout(() => setNotification(null), 6000);
        return;
      }
      try {
        await sendSeraAtendidaHoje(token, [numeroOS]);
      } catch (_) {
        /* e-mail opcional */
      }
      setOrdensServico((prev) => prev.filter((o) => String(o.numeroOS) !== String(numeroOS)));
      setSelectedOS((prev) => (prev && String(prev.numeroOS) === String(numeroOS) ? null : prev));
      await fetchOficinaData(false);
      if (modeloEq) {
        await fetchOrdensByEquipamento(modeloEq, 'PARA AMANHA', dataPrevEq);
      }
      setNotification({
        type: 'success',
        message: `OS ${numeroOS} movida para "Para hoje" (Pendente).`,
        moveToast: true,
      });
      setTimeout(() => setNotification(null), 4000);
    } catch (err) {
      setNotification({
        type: 'error',
        message: err.message || 'Erro ao mover a OS.',
        moveToast: true,
      });
      setTimeout(() => setNotification(null), 6000);
    } finally {
      setOficinaMoveLoading(false);
    }
  };

  const handleDropParaAmanhaTab = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropHoverTarget(null);
    const raw = e.dataTransfer.getData(OFICINA_DRAG_MIME) || e.dataTransfer.getData('application/oficina-os');
    const payload = parseOficinaDragPayload(raw);
    setDragOficinaCtx(null);
    if (!payload || payload.from !== 'PENDENTE') return;
    const numeroOS = payload.numeroOS;
    if (!numeroOS) return;
    if (!userCodTec) {
      setNotification({ type: 'error', message: 'Código do técnico indisponível. Atualize a página.', moveToast: true });
      setTimeout(() => setNotification(null), 5000);
      return;
    }
    const techId = String(userCodTec).padStart(4, '0');
    const modeloPendente = selectedEquipment?.modelo;
    const dataPrevPendente = selectedEquipment?.dataPrevisao || null;
    const eraPendente = selectedEquipment?.statusCard === 'PENDENTE';
    setOficinaMoveLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${base}/api/orders/update-status`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderIds: [numeroOS],
          targetSection: 'Previstas para amanhã',
          technicianId: techId,
        }),
      });
      const result = await response.json();
      const apiErrors = result.data?.errors || [];
      if (!response.ok || !result.success || apiErrors.length > 0) {
        const errMsg =
          apiErrors[0]?.error || result.message || 'Não foi possível mover a OS para "Para amanhã".';
        setNotification({ type: 'error', message: errMsg, moveToast: true });
        setTimeout(() => setNotification(null), 6000);
        return;
      }
      try {
        await sendSeraAtendidaAmanha(token, [numeroOS]);
      } catch (_) {
        /* e-mail opcional */
      }
      setOrdensServico((prev) => prev.filter((o) => String(o.numeroOS) !== String(numeroOS)));
      setSelectedOS((prev) => (prev && String(prev.numeroOS) === String(numeroOS) ? null : prev));
      await fetchOficinaData(false);
      if (eraPendente && modeloPendente) {
        await fetchOrdensByEquipamento(modeloPendente, 'PENDENTE', dataPrevPendente);
      }
      setNotification({
        type: 'success',
        message: `OS ${numeroOS} movida para "Para amanhã".`,
        moveToast: true,
      });
      setTimeout(() => setNotification(null), 4000);
    } catch (err) {
      setNotification({
        type: 'error',
        message: err.message || 'Erro ao mover a OS.',
        moveToast: true,
      });
      setTimeout(() => setNotification(null), 6000);
    } finally {
      setOficinaMoveLoading(false);
    }
  };
  
  const fetchCondicoesTodos = useCallback(async () => {
    try {
      setIsLoadingCondicoes(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${base}/api/oficina/condicoes?all=1`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const list = Array.isArray(data.data) ? data.data : [];
          setCondicoesOptions(
            [...list].sort((a, b) =>
              String(a.TB01055_NOME || '').localeCompare(String(b.TB01055_NOME || ''), 'pt-BR', { sensitivity: 'base' })
            )
          );
        } else {
          setCondicoesOptions([]);
        }
      } else {
        setCondicoesOptions([]);
      }
    } catch (error) {
      console.error('Erro ao buscar condições:', error);
      setCondicoesOptions([]);
    } finally {
      setIsLoadingCondicoes(false);
    }
  }, [base]);

  // Função para buscar dados de movimentação
  const fetchMovimentacao = async () => {
    if (!selectedOS?.numeroSerie) {
      setMovimentacaoData([]);
      return;
    }

    setIsLoadingMovimentacao(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${base}/api/oficina/movimentacao?serie=${encodeURIComponent(selectedOS.numeroSerie)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setMovimentacaoData(data.data);
        } else {
          setMovimentacaoData([]);
        }
      } else {
        setMovimentacaoData([]);
      }
    } catch (error) {
      console.error('Erro ao buscar movimentação:', error);
      setMovimentacaoData([]);
    } finally {
      setIsLoadingMovimentacao(false);
    }
  };

  // Buscar movimentação quando modal é aberto ou quando muda para aba movimentação
  useEffect(() => {
    if (showHistoryModal && activeHistoryTab === 'movimentacao' && selectedOS?.numeroSerie) {
      fetchMovimentacao();
    }
  }, [showHistoryModal, activeHistoryTab, selectedOS]);

  // Função para buscar ordens de serviço do histórico
  const fetchOrdensHistorico = async () => {
    if (!selectedOS?.numeroSerie) {
      setOrdensHistoricoData([]);
      return;
    }

    setIsLoadingOrdensHistorico(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${base}/api/oficina/ordens-serie?serie=${encodeURIComponent(selectedOS.numeroSerie)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setOrdensHistoricoData(data.data);
        } else {
          setOrdensHistoricoData([]);
        }
      } else {
        setOrdensHistoricoData([]);
      }
    } catch (error) {
      console.error('Erro ao buscar ordens do histórico:', error);
      setOrdensHistoricoData([]);
    } finally {
      setIsLoadingOrdensHistorico(false);
    }
  };

  // Buscar ordens quando modal é aberto ou quando muda para aba ordens
  useEffect(() => {
    if (showHistoryModal && activeHistoryTab === 'ordens' && selectedOS?.numeroSerie) {
      fetchOrdensHistorico();
    }
  }, [showHistoryModal, activeHistoryTab, selectedOS]);

  // Função para buscar peças trocadas
  const fetchPecasTrocadas = async () => {
    if (!selectedOS?.numeroSerie) {
      setPecasTrocadasData([]);
      return;
    }

    setIsLoadingPecasTrocadas(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${base}/api/oficina/pecas-trocadas?serie=${encodeURIComponent(selectedOS.numeroSerie)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setPecasTrocadasData(data.data);
        } else {
          setPecasTrocadasData([]);
        }
      } else {
        setPecasTrocadasData([]);
      }
    } catch (error) {
      console.error('Erro ao buscar peças trocadas:', error);
      setPecasTrocadasData([]);
    } finally {
      setIsLoadingPecasTrocadas(false);
    }
  };

  // Buscar peças trocadas quando modal é aberto ou quando muda para aba pecas
  useEffect(() => {
    if (showHistoryModal && activeHistoryTab === 'pecas' && selectedOS?.numeroSerie) {
      fetchPecasTrocadas();
    }
  }, [showHistoryModal, activeHistoryTab, selectedOS]);

  // Fechar menu de exportação ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  // Fechar dropdown de busca ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
      }
    };
    if (showSearchDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchDropdown]);

  // Função para buscar OS por patrimônio
  const buscarOSPorPatrimonio = async (patrimonio) => {
    if (!patrimonio || patrimonio.trim().length === 0) return;
    
    setIsSearchingOS(true);
    setFoundOS(null);
    setHighlightedOS(null);
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${base}/api/oficina/buscar-os-patrimonio/${encodeURIComponent(patrimonio.trim())}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const osData = data.data;
          setFoundOS(osData);
          
          // Mudar para o filtro correto se necessário
          if (osData.filtro && osData.filtro !== activeFilter) {
            setActiveFilter(osData.filtro);
            // Aguardar os dados carregarem antes de selecionar
            setTimeout(() => {
              selecionarOSEncontrada(osData);
            }, 1000);
          } else {
            selecionarOSEncontrada(osData);
          }
        } else {
          setNotification({
            message: data.message || 'Patrimônio não encontrado',
            type: 'error'
          });
          setFoundOS(null);
        }
      } else {
        setNotification({
          message: 'Erro ao buscar patrimônio',
          type: 'error'
        });
        setFoundOS(null);
      }
    } catch (error) {
      console.error('Erro ao buscar OS por patrimônio:', error);
      setNotification({
        message: 'Erro ao buscar patrimônio',
        type: 'error'
      });
      setFoundOS(null);
    } finally {
      setIsSearchingOS(false);
    }
  };

  // Função para buscar OS por série
  const buscarOSPorSerie = async (numeroSerie) => {
    if (!numeroSerie || numeroSerie.trim().length === 0) return;
    
    setIsSearchingOS(true);
    setFoundOS(null);
    setHighlightedOS(null);
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${base}/api/oficina/buscar-os-serie/${encodeURIComponent(numeroSerie.trim())}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const osData = data.data;
          setFoundOS(osData);
          
          // Mudar para o filtro correto se necessário
          if (osData.filtro && osData.filtro !== activeFilter) {
            setActiveFilter(osData.filtro);
            // Aguardar os dados carregarem antes de selecionar
            setTimeout(() => {
              selecionarOSEncontrada(osData);
            }, 1000);
          } else {
            selecionarOSEncontrada(osData);
          }
        } else {
          setNotification({
            message: data.message || 'Série não encontrada',
            type: 'error'
          });
          setFoundOS(null);
        }
      } else {
        setNotification({
          message: 'Erro ao buscar série',
          type: 'error'
        });
        setFoundOS(null);
      }
    } catch (error) {
      console.error('Erro ao buscar OS por série:', error);
      setNotification({
        message: 'Erro ao buscar série',
        type: 'error'
      });
      setFoundOS(null);
    } finally {
      setIsSearchingOS(false);
    }
  };

  // Função para buscar OS
  const buscarOS = async (codigoOS) => {
    if (!codigoOS || codigoOS.length !== 6) return;
    
    setIsSearchingOS(true);
    setFoundOS(null);
    setHighlightedOS(null);
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${base}/api/oficina/buscar-os/${codigoOS}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const osData = data.data;
          setFoundOS(osData);
          
          // Mudar para o filtro correto se necessário
          if (osData.filtro && osData.filtro !== activeFilter) {
            setActiveFilter(osData.filtro);
            // Aguardar os dados carregarem antes de selecionar
            setTimeout(() => {
              selecionarOSEncontrada(osData);
            }, 1000);
          } else {
            selecionarOSEncontrada(osData);
          }
        } else {
          setNotification({
            message: data.message || 'OS não encontrada',
            type: 'error'
          });
          setFoundOS(null);
        }
      } else {
        setNotification({
          message: 'Erro ao buscar OS',
          type: 'error'
        });
        setFoundOS(null);
      }
    } catch (error) {
      console.error('Erro ao buscar OS:', error);
      setNotification({
        message: 'Erro ao buscar OS',
        type: 'error'
      });
      setFoundOS(null);
    } finally {
      setIsSearchingOS(false);
    }
  };

  // Função para selecionar a OS encontrada
  const selecionarOSEncontrada = async (osData) => {
    if (!osData) return;
    
    // Selecionar o equipamento
    const equipment = {
      modelo: osData.modelo,
      quantidade: 1,
      statusCard: osData.statusCard
    };
    setSelectedEquipment(equipment);
    
    // Buscar ordens do equipamento
    await fetchOrdensByEquipamento(osData.modelo, osData.statusCard, null);
    
    // Aguardar os dados carregarem e então destacar a OS
    const checkAndSelect = () => {
      if (ordensServico.length > 0) {
        const osEncontrada = ordensServico.find(os => os.numeroOS === osData.numeroOS);
        if (osEncontrada) {
          setHighlightedOS(osData.numeroOS);
          setSelectedOS({
            numeroOS: osEncontrada.numeroOS,
            numeroPatrimonio: osEncontrada.patrimonio,
            numeroSerie: osEncontrada.numeroSerie,
            problema: osEncontrada.defeito,
            entrada: osEncontrada.dataAbertura
          });
        } else {
          // Tentar novamente após um tempo
          setTimeout(checkAndSelect, 200);
        }
      } else {
        // Aguardar um pouco mais se ainda não carregou
        setTimeout(checkAndSelect, 200);
      }
    };
    
    setTimeout(checkAndSelect, 300);
  };

  // Monitorar busca por OS quando digitar 6 dígitos
  useEffect(() => {
    if (searchType === 'OS' && searchValue.length === 6 && /^\d{6}$/.test(searchValue) && !isSearchingOS) {
      buscarOS(searchValue);
    } else if (searchType === 'OS' && searchValue.length > 6) {
      // Limpar busca se passar de 6 dígitos
      setSearchValue(searchValue.substring(0, 6));
    }
  }, [searchValue, searchType]);

  // Selecionar OS encontrada quando os dados das ordens carregarem
  useEffect(() => {
    if (foundOS && ordensServico.length > 0 && selectedEquipment && !highlightedOS) {
      const osEncontrada = ordensServico.find(os => os.numeroOS === foundOS.numeroOS);
      if (osEncontrada) {
        setHighlightedOS(foundOS.numeroOS);
        setSelectedOS({
          numeroOS: osEncontrada.numeroOS,
          numeroPatrimonio: osEncontrada.patrimonio,
          numeroSerie: osEncontrada.numeroSerie,
          problema: osEncontrada.defeito,
          entrada: osEncontrada.dataAbertura
        });
        // Remover animação após 2 segundos
        setTimeout(() => {
          setFoundOS(null);
        }, 2000);
      }
    }
  }, [ordensServico, foundOS, selectedEquipment]);

  // Limpar busca quando mudar o tipo
  useEffect(() => {
    if (searchType !== 'OS' && searchType !== 'Série' && searchType !== 'Patrimônio') {
      setSearchValue('');
      setFoundOS(null);
      setHighlightedOS(null);
    }
  }, [searchType]);

  // Função para exportar PDF
  const exportToPDF = () => {
    setShowExportMenu(false);
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10.5; // 30% de redução de 15mm
    let yPosition = margin;

    // Função para adicionar numeração de páginas
    const addPageNumbers = () => {
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(150, 150, 150);
        const pageText = `${i} / ${totalPages}`;
        const pageTextWidth = pdf.getTextWidth(pageText);
        pdf.text(pageText, pageWidth - margin - pageTextWidth, pageHeight - 5);
      }
    };

    // Header com dados do equipamento
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 41, 59);
    pdf.text(`${selectedEquipment?.modelo || 'Equipamento'}`, margin, yPosition);
    yPosition += 6;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 116, 139);
    if (selectedOS) {
      pdf.text(`Série: ${selectedOS.numeroSerie || '-'}`, margin, yPosition);
      yPosition += 5;
      pdf.text(`Patrimônio: ${selectedOS.numeroPatrimonio || '-'}`, margin, yPosition);
      yPosition += 8;
    }

    // Tabela - removendo colunas Motivo e Laudo
    const colWidths = [20, 25, 20, 40, 30, 30, 30, 25];
    const headers = ['OS', 'Tipo', 'Abertura', 'Cliente', 'Solicitante', 'Técnico', 'Condição Final', 'Fechamento'];
    
    // Cabeçalho da tabela
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(100, 116, 139);
    let xPos = margin;
    headers.forEach((header, idx) => {
      pdf.text(header, xPos, yPosition);
      xPos += colWidths[idx];
    });
    yPosition += 6;
    pdf.setDrawColor(241, 245, 249);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 3;

    // Dados da tabela
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(30, 41, 59);
    
    filteredOrdens.forEach((ordem, index) => {
      if (yPosition > pageHeight - 50) {
        pdf.addPage();
        yPosition = margin;
      }
      
      const rowY = yPosition;
      xPos = margin;
      const rowData = [
        ordem.TB02115_CODIGO || '-',
        ordem.TIPO_OS || '-',
        ordem.TB02115_DATA || '-',
        ordem.TB01008_NOME || '-',
        ordem.TB02115_SOLICITANTE || '-',
        ordem.TB01024_NOME || '-',
        ordem.TB01055_NOME || '-',
        ordem.TB02122_DATAFIM || '-'
      ];
      
      rowData.forEach((data, idx) => {
        const maxWidth = colWidths[idx] - 2;
        const text = String(data);
        const truncatedText = pdf.splitTextToSize(text, maxWidth);
        pdf.text(truncatedText[0] || text.substring(0, Math.floor(maxWidth / 2)), xPos, yPosition);
        xPos += colWidths[idx];
      });
      yPosition += 4;
      
      // Motivo
      if (yPosition > pageHeight - 30) {
        pdf.addPage();
        yPosition = margin;
      }
      pdf.setFontSize(6);
      pdf.setFont('helvetica', 'italic');
      pdf.setTextColor(71, 85, 105);
      const motivo = String(ordem.TB02115_NOME || '-');
      const motivoLines = pdf.splitTextToSize(`Motivo: ${motivo}`, pageWidth - margin * 2);
      pdf.text(motivoLines, margin + 2, yPosition);
      yPosition += motivoLines.length * 3;
      
      // Laudo
      if (yPosition > pageHeight - 30) {
        pdf.addPage();
        yPosition = margin;
      }
      const laudo = String(ordem.TB02122_OBS || '-');
      const laudoLines = pdf.splitTextToSize(`Laudo: ${laudo}`, pageWidth - margin * 2);
      pdf.text(laudoLines, margin + 2, yPosition);
      yPosition += laudoLines.length * 3 + 2;
      
      // Borda separadora entre OS (exceto na última)
      if (index < filteredOrdens.length - 1) {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = margin;
        }
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.2);
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 3;
      }
      
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(30, 41, 59);
    });

    // Adicionar numeração de páginas
    addPageNumbers();

    pdf.save(`ordens-servico-${selectedOS?.numeroSerie || 'relatorio'}.pdf`);
  };

  // Função para exportar Excel
  const exportToExcel = () => {
    setShowExportMenu(false);
    // Criar planilha XLSX
    const headers = ['OS', 'Tipo', 'Abertura', 'Cliente', 'Solicitante', 'Motivo', 'Técnico', 'Condição Final', 'Fechamento', 'Laudo'];
    const data = filteredOrdens.map(ordem => [
      ordem.TB02115_CODIGO || '',
      ordem.TIPO_OS || '',
      ordem.TB02115_DATA || '',
      ordem.TB01008_NOME || '',
      ordem.TB02115_SOLICITANTE || '',
      ordem.TB02115_NOME || '',
      ordem.TB01024_NOME || '',
      ordem.TB01055_NOME || '',
      ordem.TB02122_DATAFIM || '',
      ordem.TB02122_OBS || ''
    ]);
    
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ordens de Serviço');
    
    // Ajustar largura das colunas
    const colWidths = headers.map(() => ({ wch: 15 }));
    worksheet['!cols'] = colWidths;
    
    XLSX.writeFile(workbook, `ordens-servico-${selectedOS?.numeroSerie || 'relatorio'}.xlsx`);
  };
  
  const startRevisao = async () => {
    if (!selectedOS?.numeroOS) {
      setNotification({
        message: 'Nenhuma ordem de serviço selecionada',
        type: 'error'
      });
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${base}/api/oficina/iniciar-revisao`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          numeroOS: selectedOS.numeroOS
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setNotification({
          message: data.message || `OS ${selectedOS.numeroOS} iniciada com sucesso`,
          type: 'success'
        });
        
        // Fechar painel de detalhes
        setSelectedOS(null);
        
        // Atualizar dados (refresh)
        await handleRefresh();
        
        // Fechar notificação após animação
        setTimeout(() => {
          setNotification(null);
        }, 3000);
      } else {
        throw new Error(data.message || 'Erro ao iniciar revisão');
      }
    } catch (error) {
      console.error('Erro ao iniciar revisão:', error);
      setNotification({
        message: error.message || 'Erro ao iniciar revisão',
        type: 'error'
      });
      setTimeout(() => {
        setNotification(null);
      }, 3000);
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Função para lidar com ação do botão (iniciar/finalizar revisão)
  const handleActionButton = async () => {
    const statusBadge = getStatusBadge();
    
    if (statusBadge.texto === 'Iniciar revisão') {
      await startRevisao();
    } else if (statusBadge.texto === 'Finalizar revisão') {
      setIsProcessing(true);
      
      // Resetar formulário antes de abrir
      setClosureForm({
        dataTermino: '',
        horaTermino: '',
        condicaoFinal: '',
        codigoCondicao: '',
        contadores: {
          a4pb: '',
          a4cor: '',
          dig: '',
          a3pb: '',
          a3cor: ''
        },
        laudoTecnico: ''
      });
      setCondicaoSelectOpen(false);
      setShowA3PB(false);
      setShowA3Cor(false);
      
      // Simular processamento antes de abrir formulário
      setTimeout(() => {
        setIsProcessing(false);
        setShowClosureForm(true);
      }, 1000);
    }
  };
  
  // Função para concluir o fechamento
  const handleConcluirOS = async () => {
    if (!selectedOS?.numeroOS) {
      setNotification({
        message: 'Nenhuma ordem de serviço selecionada',
        type: 'error'
      });
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const token = localStorage.getItem('authToken');
      
      // Formatar dataFim: yyyy-MM-dd 00:00:00.000
      const dataFim = closureForm.dataTermino ? `${closureForm.dataTermino} 00:00:00.000` : null;
      
      // Formatar horaFim: hh:mm (já está no formato correto)
      const horaFim = closureForm.horaTermino || null;

      const body = {
        numeroOS: selectedOS.numeroOS,
        contadorPB: closureForm.contadores.a4pb || '0',
        contadorCor: closureForm.contadores.a4cor || '0',
        contadorDig: closureForm.contadores.dig || '0',
        contadorA3PB: closureForm.contadores.a3pb || '0',
        contadorA3Cor: closureForm.contadores.a3cor || '0',
        codCondicao: closureForm.codigoCondicao,
        laudoTecnico: closureForm.laudoTecnico,
        dataFim: dataFim,
        horaFim: horaFim
      };

      const response = await fetch(`${base}/api/oficina/finalizar-os`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      const data = await response.json();
      
      if (data.success && data.resultado && data.resultado.Sucesso === 1) {
        setNotification({
          message: data.resultado.Mensagem || `OS ${selectedOS.numeroOS} concluída com sucesso`,
          type: 'success'
        });
        let codstatus = '00';
        try {
          const configRes = await fetch(`${base}/api/config/troca-status-conclusao`, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
          });
          const configData = await configRes.json().catch(() => ({}));
          codstatus = (configData.success && configData.data && configData.data.codstatus) ? String(configData.data.codstatus) : '00';
        } catch (_) {
          codstatus = '00';
        }
        if (codstatus === '01') {
          setShowClosureForm(false);
          setClosureForm({
            dataTermino: '',
            horaTermino: '',
            condicaoFinal: '',
            codigoCondicao: '',
            contadores: { a4pb: '', a4cor: '', dig: '', a3pb: '', a3cor: '' },
            laudoTecnico: ''
          });
          setShowA3PB(false);
          setShowA3Cor(false);
          setShowAlterarStatusModal(true);
          setSelectedCodStatusConclusao('');
          setOpcoesStatusConclusao([]);
          setAlterarStatusFeedback(null);
          setTimeout(() => setNotification(null), 2000);
        } else {
          setShowClosureForm(false);
          setSelectedOS(null);
          setClosureForm({
            dataTermino: '',
            horaTermino: '',
            condicaoFinal: '',
            codigoCondicao: '',
            contadores: { a4pb: '', a4cor: '', dig: '', a3pb: '', a3cor: '' },
            laudoTecnico: ''
          });
          setShowA3PB(false);
          setShowA3Cor(false);
          await handleRefresh();
          setTimeout(() => setNotification(null), 3000);
        }
      } else {
        throw new Error(data.resultado?.Mensagem || data.message || 'Erro ao concluir OS');
      }
    } catch (error) {
      console.error('Erro ao concluir OS:', error);
      setNotification({
        message: error.message || 'Erro ao concluir ordem de serviço',
        type: 'error'
      });
      setTimeout(() => {
        setNotification(null);
      }, 3000);
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (!showAlterarStatusModal) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    let cancelled = false;
    setIsOpcoesStatusLoading(true);
    fetch(`${base}/api/oficina/opcoes-status-conclusao`, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } })
      .then((r) => r.json())
      .then((d) => { if (!cancelled && d.success && Array.isArray(d.data)) setOpcoesStatusConclusao(d.data); })
      .catch(() => { if (!cancelled) setOpcoesStatusConclusao([]); })
      .finally(() => { if (!cancelled) setIsOpcoesStatusLoading(false); });
    return () => { cancelled = true; };
  }, [showAlterarStatusModal, base]);

  const handleConfirmarAlterarStatus = async () => {
    if (!selectedOS?.numeroOS || !selectedCodStatusConclusao || alterarStatusLoading) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setAlterarStatusLoading(true);
    setAlterarStatusFeedback(null);
    try {
      const res = await fetch(`${base}/api/oficina/alterar-status-conclusao`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ numeroOS: selectedOS.numeroOS, codNovoStatus: selectedCodStatusConclusao })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setAlterarStatusFeedback('success');
        setTimeout(() => {
          setShowAlterarStatusModal(false);
          setSelectedOS(null);
          handleRefresh();
        }, 1800);
      } else {
        setAlterarStatusFeedback('error');
        setTimeout(() => setAlterarStatusFeedback(null), 2800);
      }
    } catch {
      setAlterarStatusFeedback('error');
      setTimeout(() => setAlterarStatusFeedback(null), 2800);
    } finally {
      setAlterarStatusLoading(false);
    }
  };
  
  useEffect(() => {
    if (!showClosureForm) {
      setCondicaoSelectOpen(false);
      return;
    }
    fetchCondicoesTodos();
  }, [showClosureForm, fetchCondicoesTodos]);

  useEffect(() => {
    if (!condicaoSelectOpen) return;
    const onDoc = (e) => {
      if (condicaoSelectRef.current && !condicaoSelectRef.current.contains(e.target)) {
        setCondicaoSelectOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [condicaoSelectOpen]);

  // Função para converter data dd/MM/yyyy para Date
  const parseDate = (dateString) => {
    const [day, month, year] = dateString.split('/');
    return new Date(year, month - 1, day);
  };
  
  // Listas únicas para filtros (baseado nos dados reais)
  const uniqueClientes = [...new Set(ordensHistoricoData.map(o => o.TB01008_NOME || '').filter(Boolean))].sort();
  const uniqueTipos = [...new Set(ordensHistoricoData.map(o => o.TIPO_OS || '').filter(Boolean))].sort();
  const uniqueTecnicos = [...new Set(ordensHistoricoData.map(o => o.TB01024_NOME || '').filter(Boolean))].sort();
  
  // Mapear tipos para nomes completos
  const tipoNomes = {
    'I': 'Instalação',
    'E': 'Estoque',
    'B': 'Balcão',
    'A': 'Aferição',
    'R': 'Recarga/Retorno',
    'D': 'Desinstalação',
    'P': 'Preventiva',
    'N': 'Corretiva'
  };
  
  // Filtrar listas de seleção baseado na busca
  const filteredClientesList = uniqueClientes.filter(c => 
    c.toLowerCase().includes(clienteSearchText.toLowerCase())
  );
  
  const filteredTiposList = uniqueTipos.filter(t => 
    t.toLowerCase().includes(tipoSearchText.toLowerCase())
  );
  
  const filteredTecnicosList = uniqueTecnicos.filter(t => 
    t.toLowerCase().includes(tecnicoSearchText.toLowerCase())
  );
  
  // Listas únicas para peças trocadas (baseado nos dados reais)
  const uniqueReferencias = [...new Set(pecasTrocadasData.map(p => p.TB01010_REFERENCIA || '').filter(Boolean))].sort();
  const uniqueProdutos = [...new Set(pecasTrocadasData.map(p => p.TB01010_NOME || '').filter(Boolean))].sort();
  
  const filteredReferenciasList = uniqueReferencias.filter(r => 
    r.toLowerCase().includes(referenciaSearchText.toLowerCase())
  );
  
  const filteredProdutosList = uniqueProdutos.filter(p => 
    p.toLowerCase().includes(produtoSearchText.toLowerCase())
  );
  
  // Filtrar peças trocadas
  const filteredPecas = pecasTrocadasData.filter(peca => {
    let passDataFilter = true;
    let passReferenciaFilter = true;
    let passProdutoFilter = true;
    
    if (pecasDataStart && pecasDataEnd) {
      const dataPeca = parseDate(peca.TB02021_DATA || '');
      const startDate = new Date(pecasDataStart);
      const endDate = new Date(pecasDataEnd);
      passDataFilter = dataPeca >= startDate && dataPeca <= endDate;
    }
    
    if (selectedReferencias.length > 0) {
      passReferenciaFilter = selectedReferencias.includes(peca.TB01010_REFERENCIA || '');
    }
    
    if (selectedProdutos.length > 0) {
      passProdutoFilter = selectedProdutos.includes(peca.TB01010_NOME || '');
    }
    
    return passDataFilter && passReferenciaFilter && passProdutoFilter;
  });
  
  // Filtrar ordens de serviço
  const filteredOrdens = ordensHistoricoData.filter(ordem => {
    let passAberturaFilter = true;
    let passFechamentoFilter = true;
    let passClienteFilter = true;
    let passTipoFilter = true;
    let passTecnicoFilter = true;
    
    if (aberturaStart && aberturaEnd) {
      const dataAbertura = parseDate(ordem.TB02115_DATA || '');
      const startDate = new Date(aberturaStart);
      const endDate = new Date(aberturaEnd);
      passAberturaFilter = dataAbertura >= startDate && dataAbertura <= endDate;
    }
    
    if (fechamentoStart && fechamentoEnd) {
      const dataFechamento = parseDate(ordem.TB02122_DATAFIM || '');
      const startDate = new Date(fechamentoStart);
      const endDate = new Date(fechamentoEnd);
      passFechamentoFilter = dataFechamento >= startDate && dataFechamento <= endDate;
    }
    
    if (selectedClientes.length > 0) {
      passClienteFilter = selectedClientes.includes(ordem.TB01008_NOME || '');
    }
    
    if (selectedTipos.length > 0) {
      passTipoFilter = selectedTipos.includes(ordem.TIPO_OS || '');
    }
    
    if (selectedTecnicos.length > 0) {
      passTecnicoFilter = selectedTecnicos.includes(ordem.TB01024_NOME || '');
    }
    
    return passAberturaFilter && passFechamentoFilter && passClienteFilter && passTipoFilter && passTecnicoFilter;
  });
  
  // Função para determinar o texto e cor do badge de status
  const getStatusBadge = () => {
    if (!selectedEquipment) return { texto: 'Em andamento', cor: '#dbeafe' };
    
    // Finalizar revisão (EM ANDAMENTO)
    if (selectedEquipment.statusCard === 'EM ANDAMENTO') {
      return { texto: 'Finalizar revisão', cor: '#7BF1A8', textColor: '#065f46' };
    }
    
    // Iniciar revisão (PENDENTE na sessão "Para hoje")
    if (activeFilter === 'para-hoje' && selectedEquipment.statusCard === 'PENDENTE') {
      return { texto: 'Iniciar revisão', cor: '#dbeafe', textColor: '#1e40af' };
    }
    
    // Iniciar revisão (qualquer coluna em "Para amanhã" e "Próximos dias")
    if (activeFilter === 'para-amanha' || activeFilter === 'proximos-dias') {
      return { texto: 'Iniciar revisão', cor: '#dbeafe', textColor: '#1e40af' };
    }
    
    return { texto: 'Em andamento', cor: '#dbeafe', textColor: '#1e40af' };
  };
  
  // Função para gerar ordens de serviço de exemplo
  const gerarOrdensDeServico = (quantidade, modelo) => {
    const ordens = [];
    const problemas = ['Não liga', 'Papel encravado', 'Impressão manchada', 'Erro de conexão', 'Toner vazio', 'Barulho excessivo', 'Mensagem de erro', 'Lentidão', 'Não imprime colorido', 'Wifi não conecta'];
    
    for (let i = 0; i < quantidade; i++) {
      const numeroOS = Math.floor(100000 + Math.random() * 900000);
      ordens.push({
        numeroOS: numeroOS,
        numeroPatrimonio: `PAT-${Math.floor(10000 + Math.random() * 90000)}`,
        numeroSerie: `SN-${Math.floor(100000 + Math.random() * 900000)}`,
        problema: problemas[i % problemas.length],
        entrada: `${String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')}/01/2026`,
        tecnico: i % 2 === 0 ? 'Técnico A' : 'Técnico B'
      });
    }
    return ordens;
  };
  
  // Função para calcular o próximo dia útil
  const getProximoDiaUtil = () => {
    const hoje = new Date();
    const diaSemana = hoje.getDay(); // 0 = Domingo, 1 = Segunda, ..., 6 = Sábado
    let diasParaAdicionar = 1; // Padrão: amanhã
    
    if (diaSemana === 5) { // Sexta-feira
      diasParaAdicionar = 3; // Pular para segunda-feira
    } else if (diaSemana === 6) { // Sábado
      diasParaAdicionar = 2; // Pular para segunda-feira
    }
    
    const proximoDiaUtil = new Date(hoje);
    proximoDiaUtil.setDate(hoje.getDate() + diasParaAdicionar);
    
    // Formatar em dd/MM/yyyy
    const dia = String(proximoDiaUtil.getDate()).padStart(2, '0');
    const mes = String(proximoDiaUtil.getMonth() + 1).padStart(2, '0');
    const ano = proximoDiaUtil.getFullYear();
    
    return `${dia}/${mes}/${ano}`;
  };
  
  // Função para calcular dias úteis a frente (a partir do próximo dia útil)
  const getDiaUtilAFrente = (numeroDiasUteis) => {
    const hoje = new Date();
    let diasAdicionados = 0;
    let dataAtual = new Date(hoje);
    
    while (diasAdicionados < numeroDiasUteis) {
      dataAtual.setDate(dataAtual.getDate() + 1);
      const diaSemana = dataAtual.getDay();
      // Se não for sábado (6) nem domingo (0), é dia útil
      if (diaSemana !== 0 && diaSemana !== 6) {
        diasAdicionados++;
      }
    }
    
    const dia = String(dataAtual.getDate()).padStart(2, '0');
    const mes = String(dataAtual.getMonth() + 1).padStart(2, '0');
    const ano = dataAtual.getFullYear();
    
    return `${dia}/${mes}/${ano}`;
  };
  
  // Dados agora vêm do backend via estados impressoras e pendentes
  
  // Calcular totais de equipamentos
  const totalEmAndamento = impressoras.reduce((acc, imp) => acc + (imp.quantidade || 0), 0);
  const totalPausado = pausados.reduce((acc, imp) => acc + (imp.quantidade || 0), 0);
  const totalPendente = pendentes.reduce((acc, imp) => acc + (imp.quantidade || 0), 0);
  
  return (
    <div className="oficina-container">
      {oficinaMoveLoading && (
        <div className="oficina-move-overlay" role="status" aria-live="polite">
          <div className="oficina-move-overlay__inner">
            <div className="oficina-move-overlay__spinner" />
            <span className="oficina-move-overlay__text">Atualizando…</span>
          </div>
        </div>
      )}
      <FutureDateModalOficina
        isOpen={showFuturaModal}
        data={futuraModalData}
        onClose={() => {
          setShowFuturaModal(false);
          setFuturaModalData(null);
        }}
        onConfirm={handleFuturaModalConfirm}
      />
      <div className="oficina-filter-section">
        {/* Chaveador de seleção e botão de atualização */}
        <div className="oficina-filter-toolbar">
        <div className="oficina-filter-toolbar-left" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div
            className={`oficina-selection-switch ${isLoading ? 'skeleton' : ''} ${foundOS ? 'highlight-animation' : ''} ${dragOsPendente ? 'oficina-selection-switch--drag-source' : ''}`}
          >
            <button
              type="button"
              className={`selection-option ${activeFilter === 'para-hoje' ? 'active' : ''} ${dropHoverTarget === 'hoje' ? 'selection-option--drop-target' : ''}`}
              onClick={() => setActiveFilter('para-hoje')}
              disabled={isLoading}
              onDragOver={(e) => {
                if (dragOficinaCtx?.from === 'PENDENTE') {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'none';
                  return;
                }
                if (dragOficinaCtx?.from === 'PARA_AMANHA') {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                }
              }}
              onDragEnter={() => dragOficinaCtx?.from === 'PARA_AMANHA' && setDropHoverTarget('hoje')}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  setDropHoverTarget((t) => (t === 'hoje' ? null : t));
                }
              }}
              onDrop={handleDropParaHojeFromAmanha}
            >
              Para hoje
              {filterCounts['para-hoje'] > 0 && (
                <span className="count-badge">{filterCounts['para-hoje']}</span>
              )}
            </button>
            <button
              type="button"
              className={`selection-option ${activeFilter === 'para-amanha' ? 'active' : ''} ${dropHoverTarget === 'amanha' ? 'selection-option--drop-target' : ''}`}
              onClick={() => setActiveFilter('para-amanha')}
              disabled={isLoading}
              onDragOver={(e) => {
                if (dragOficinaCtx?.from !== 'PENDENTE') return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDragEnter={() => dragOficinaCtx?.from === 'PENDENTE' && setDropHoverTarget('amanha')}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  setDropHoverTarget((t) => (t === 'amanha' ? null : t));
                }
              }}
              onDrop={handleDropParaAmanhaTab}
            >
              Para amanhã
              {filterCounts['para-amanha'] > 0 && (
                <span className="count-badge">{filterCounts['para-amanha']}</span>
              )}
            </button>
            <button
              type="button"
              className={`selection-option ${activeFilter === 'proximos-dias' ? 'active' : ''} ${dropHoverTarget === 'proximos' ? 'selection-option--drop-target' : ''}`}
              onClick={() => setActiveFilter('proximos-dias')}
              disabled={isLoading}
              onDragOver={(e) => {
                if (!dragOficinaCtx || (dragOficinaCtx.from !== 'PENDENTE' && dragOficinaCtx.from !== 'PARA_AMANHA')) {
                  return;
                }
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDragEnter={() =>
                dragOficinaCtx &&
                (dragOficinaCtx.from === 'PENDENTE' || dragOficinaCtx.from === 'PARA_AMANHA') &&
                setDropHoverTarget('proximos')
              }
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  setDropHoverTarget((t) => (t === 'proximos' ? null : t));
                }
              }}
              onDrop={handleDropProximosTab}
            >
              Próximos dias
              {filterCounts['proximos-dias'] > 0 && (
                <span className="count-badge">{filterCounts['proximos-dias']}</span>
              )}
            </button>
          </div>
          
          <button 
            className="refresh-button"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshCcw size={14} strokeWidth={2} className={isLoading ? 'spinning' : ''} />
            <span>Atualizar dados</span>
          </button>
          
          {/* Campo de busca */}
          <div className="search-input-wrapper" ref={searchDropdownRef}>
            <div className="search-input-container">
              <div 
                className="search-type-selector"
                onClick={() => setShowSearchDropdown(!showSearchDropdown)}
              >
                <span className="search-type-text">{searchType}</span>
                <ChevronDown size={14} className={`search-chevron ${showSearchDropdown ? 'open' : ''}`} />
              </div>
              {showSearchDropdown && (
                <div className="search-dropdown">
                  <div 
                    className="search-dropdown-option"
                    onClick={() => {
                      setSearchType('Buscar por...');
                      setShowSearchDropdown(false);
                    }}
                  >
                    Buscar por...
                  </div>
                  <div 
                    className="search-dropdown-option"
                    onClick={() => {
                      setSearchType('OS');
                      setShowSearchDropdown(false);
                    }}
                  >
                    OS
                  </div>
                  <div 
                    className="search-dropdown-option"
                    onClick={() => {
                      setSearchType('Série');
                      setShowSearchDropdown(false);
                    }}
                  >
                    Série
                  </div>
                  <div 
                    className="search-dropdown-option"
                    onClick={() => {
                      setSearchType('Patrimônio');
                      setShowSearchDropdown(false);
                    }}
                  >
                    Patrimônio
                  </div>
                </div>
              )}
              <div className="search-input-divider"></div>
              <input
                type="text"
                className="search-input-field"
                placeholder={searchType === 'Buscar por...' ? 'Selecione o tipo de busca' : searchType === 'OS' ? 'Digite 6 dígitos...' : searchType === 'Série' ? 'Digite a série e pressione Enter...' : searchType === 'Patrimônio' ? 'Digite o patrimônio e pressione Enter...' : `Digite ${searchType.toLowerCase()}...`}
                value={searchValue}
                onChange={(e) => {
                  const value = e.target.value;
                  if (searchType === 'OS') {
                    // Permitir apenas números e limitar a 6 dígitos
                    const numericValue = value.replace(/\D/g, '').substring(0, 6);
                    setSearchValue(numericValue);
                  } else {
                    setSearchValue(value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchValue.trim().length > 0) {
                    e.preventDefault();
                    if (searchType === 'Série') {
                      buscarOSPorSerie(searchValue);
                    } else if (searchType === 'Patrimônio') {
                      buscarOSPorPatrimonio(searchValue);
                    }
                  }
                }}
                disabled={searchType === 'Buscar por...'}
                maxLength={searchType === 'OS' ? 6 : undefined}
              />
              <Search size={14} className="search-icon" />
            </div>
          </div>
        </div>
        <div
          className="oficina-beta-notice"
          role="status"
          title="Este módulo está em fase de testes."
        >
          <Info size={13} strokeWidth={2} className="oficina-beta-notice-icon" aria-hidden />
          <span>Este módulo está em fase de testes.</span>
        </div>
        </div>

        <div
          className={`oficina-body-split ${selectedEquipment ? 'oficina-body-split--with-rail' : ''}`}
        >
          <div className="oficina-main-column">
        {/* Área dos cards */}
        <div className={`oficina-cards-container ${isLoading ? 'skeleton' : ''}`}>
          {/* Card 1 - Em Andamento (apenas para "Para hoje") */}
          {activeFilter === 'para-hoje' && (
            <div className="oficina-card filled">
              <div className="card-header">
                <div className="status-section">
                  <span className="status-label">EM ANDAMENTO</span>
                </div>
                <span className="total-count">{totalEmAndamento}</span>
              </div>
              <div className="card-body card-body-groups">
                {isLoading ? (
                  <div style={{ padding: '10px', textAlign: 'center', color: '#94a3b8' }}>Carregando...</div>
                ) : impressoras.length > 0 ? (
                  impressoras.map((impressora, index) => (
                    <div 
                      key={index}
                      className={`equipment-row ${selectedEquipment?.modelo === impressora.modelo && selectedEquipment?.statusCard === 'EM ANDAMENTO' ? 'selected' : ''}`}
                      onClick={() => handleEquipmentClick(impressora.modelo, impressora.quantidade, 'EM ANDAMENTO')}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="equipment-name">{impressora.modelo}</span>
                      <span className="equipment-count">{impressora.quantidade}</span>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '10px', textAlign: 'center', color: '#94a3b8' }}>Nenhum registro</div>
                )}
              </div>
            </div>
          )}
          
          {/* Cards 2, 3, 4 e 5 - Dependem do filtro ativo */}
          {activeFilter === 'para-hoje' ? (
            <>
              {/* Card 2 - Pausado */}
              <div className="oficina-card filled">
                <div className="card-header">
                  <div className="status-section">
                    <span className="status-label">PAUSADO</span>
                  </div>
                  <span className="total-count">{totalPausado}</span>
                </div>
                <div className="card-body card-body-groups">
                  {isLoading ? (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#94a3b8' }}>Carregando...</div>
                  ) : pausados.length > 0 ? (
                    pausados.map((equipamento, index) => (
                      <div 
                        key={index}
                        className={`equipment-row ${selectedEquipment?.modelo === equipamento.modelo && selectedEquipment?.statusCard === 'PAUSADO' ? 'selected' : ''}`}
                        onClick={() => handleEquipmentClick(equipamento.modelo, equipamento.quantidade, 'PAUSADO')}
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="equipment-name">{equipamento.modelo}</span>
                        <span className="equipment-count">{equipamento.quantidade}</span>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#94a3b8' }}>Nenhum registro</div>
                  )}
                </div>
              </div>
              {/* Card 3 - Pendente */}
              <div className="oficina-card filled">
                <div className="card-header">
                  <div className="status-section">
                    <span className="status-label">PENDENTE</span>
                  </div>
                  <span className="total-count">{totalPendente}</span>
                </div>
                <div className="card-body card-body-groups">
                  {isLoading ? (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#94a3b8' }}>Carregando...</div>
                  ) : pendentes.length > 0 ? (
                    pendentes.map((equipamento, index) => (
                      <div 
                        key={index}
                        className={`equipment-row ${selectedEquipment?.modelo === equipamento.modelo && selectedEquipment?.statusCard === 'PENDENTE' ? 'selected' : ''}`}
                        onClick={() => handleEquipmentClick(equipamento.modelo, equipamento.quantidade, 'PENDENTE')}
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="equipment-name">{equipamento.modelo}</span>
                        <span className="equipment-count">{equipamento.quantidade}</span>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#94a3b8' }}>Nenhum registro</div>
                  )}
                </div>
              </div>
              {/* Card 4 - vazio */}
              <div className="oficina-card empty"></div>
            </>
          ) : activeFilter === 'para-amanha' ? (
            <>
              {/* Card único - Data do próximo dia útil */}
              <div className="oficina-card filled">
                <div className="card-header">
                  <div className="status-section">
                    <span className="status-label">{getProximoDiaUtil()}</span>
                  </div>
                  <span className="total-count">{paraAmanha.reduce((acc, e) => acc + (e.quantidade || 0), 0)}</span>
                </div>
                <div className="card-body card-body-groups">
                  {isLoading ? (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#94a3b8' }}>Carregando...</div>
                  ) : paraAmanha.length > 0 ? (
                    paraAmanha.map((equipamento, index) => (
                      <div 
                        key={index}
                        className={`equipment-row ${selectedEquipment?.modelo === equipamento.modelo && selectedEquipment?.statusCard === 'PARA AMANHA' ? 'selected' : ''}`}
                        onClick={() => handleEquipmentClick(equipamento.modelo, equipamento.quantidade, 'PARA AMANHA')}
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="equipment-name">{equipamento.modelo}</span>
                        <span className="equipment-count">{equipamento.quantidade}</span>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#94a3b8' }}>Nenhum registro</div>
                  )}
                </div>
              </div>
              {/* Cards 2, 3, 4 - vazios */}
              <div className="oficina-card empty"></div>
              <div className="oficina-card empty"></div>
              <div className="oficina-card empty"></div>
            </>
          ) : activeFilter === 'proximos-dias' ? (
            <>
              {/* Cards dinâmicos por data (com previsão) - ordenados por data crescente */}
              {proximosDias.comPrevisao
                .filter(item => {
                  if (!item.equipamentos || item.equipamentos.length === 0) return false;
                  const total = item.equipamentos.reduce((acc, e) => acc + (Number(e.quantidade) || 0), 0);
                  return total > 0;
                })
                .map((item, index) => {
                  const total = item.equipamentos.reduce((acc, e) => acc + (Number(e.quantidade) || 0), 0);
                  if (total === 0) return null;
                  return (
                    <div key={index} className="oficina-card filled">
                      <div className="card-header">
                        <div className="status-section">
                          <span className="status-label">{item.data}</span>
                        </div>
                        <span className="total-count">{total}</span>
                      </div>
                      <div className="card-body card-body-groups">
                        {isLoading ? (
                          <div style={{ padding: '10px', textAlign: 'center', color: '#94a3b8' }}>Carregando...</div>
                        ) : item.equipamentos && item.equipamentos.length > 0 ? (
                          item.equipamentos.map((equipamento, idx) => (
                            <div 
                              key={idx}
                              className={`equipment-row ${selectedEquipment?.modelo === equipamento.modelo && selectedEquipment?.statusCard === 'PROXIMOS_DIAS' && selectedEquipment?.dataPrevisao === item.data ? 'selected' : ''}`}
                              onClick={() => handleEquipmentClick(equipamento.modelo, equipamento.quantidade, 'PROXIMOS_DIAS', item.data)}
                              style={{ cursor: 'pointer' }}
                            >
                              <span className="equipment-name">{equipamento.modelo}</span>
                              <span className="equipment-count">{equipamento.quantidade}</span>
                            </div>
                          ))
                        ) : null}
                      </div>
                    </div>
                  );
                })
                .filter(Boolean)}
              
              {/* Card Sem Previsão */}
              {proximosDias.semPrevisao.length > 0 && (
                <div className="oficina-card filled">
                  <div className="card-header">
                    <div className="status-section">
                      <span className="status-label">SEM PREVISÃO</span>
                    </div>
                    <span className="total-count">{proximosDias.semPrevisao.reduce((acc, e) => acc + (e.quantidade || 0), 0)}</span>
                  </div>
                  <div className="card-body card-body-groups">
                    {isLoading ? (
                      <div style={{ padding: '10px', textAlign: 'center', color: '#94a3b8' }}>Carregando...</div>
                    ) : (
                      proximosDias.semPrevisao.map((equipamento, index) => (
                        <div 
                          key={index}
                          className={`equipment-row ${selectedEquipment?.modelo === equipamento.modelo && selectedEquipment?.statusCard === 'PROXIMOS_DIAS' && selectedEquipment?.dataPrevisao === 'SEM PREVISÃO' ? 'selected' : ''}`}
                          onClick={() => handleEquipmentClick(equipamento.modelo, equipamento.quantidade, 'PROXIMOS_DIAS', 'SEM PREVISÃO')}
                          style={{ cursor: 'pointer' }}
                        >
                          <span className="equipment-name">{equipamento.modelo}</span>
                          <span className="equipment-count">{equipamento.quantidade}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Cards vazios para outras sessões */}
              <div className="oficina-card empty"></div>
              <div className="oficina-card empty"></div>
              <div className="oficina-card empty"></div>
            </>
          )}
        </div>

        {/* Seção de Detalhamento de Ordens de Serviço */}
        {selectedEquipment && (
          <div className="os-detailing-section">
            <div className="os-detailing-header">
              <input 
                type="text" 
                className="os-search-input"
                placeholder="Pesquisar informações..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
              />
            </div>
            <div className="os-list-external">
              {isLoadingOrdens ? (
                <div className="os-list-skeleton">
                  {[...Array(3)].map((_, index) => (
                    <div key={index} className="os-card-skeleton">
                      <div className="skeleton-row"></div>
                      <div className="skeleton-row"></div>
                    </div>
                  ))}
                </div>
              ) : ordensServico.length > 0 ? (
                ordensServico
                  .filter(os => {
                    if (!searchFilter) return true;
                    const searchLower = searchFilter.toLowerCase();
                    const nomeCliente = (os.tb01008Nome ?? os.TB01008_NOME ?? '').toString();
                    return (
                      os.numeroOS.toString().toLowerCase().includes(searchLower) ||
                      os.patrimonio.toLowerCase().includes(searchLower) ||
                      os.numeroSerie.toLowerCase().includes(searchLower) ||
                      os.defeito.toLowerCase().includes(searchLower) ||
                      nomeCliente.toLowerCase().includes(searchLower) ||
                      os.dataAbertura.includes(searchFilter) ||
                      (os.calcPrevisaoFmt && String(os.calcPrevisaoFmt).toLowerCase().includes(searchLower))
                    );
                  })
                  .map((os, osIndex) => {
                    const nomeCliente = (os.tb01008Nome ?? os.TB01008_NOME ?? '').toString().trim();
                    const previsaoAtrasada = Number(os.calcPrevisaoAtrasada) === 1 || os.calcPrevisaoAtrasada === true;
                    const osCardDragTitle =
                      activeFilter === 'para-amanha'
                        ? 'Arraste para "Para hoje" ou "Próximos dias"'
                        : 'Arraste para "Para amanhã" ou "Próximos dias"';
                    return (
                      <div 
                        key={osIndex} 
                        draggable={canDragOficinaOs}
                        title={canDragOficinaOs ? osCardDragTitle : undefined}
                        className={`os-card ${selectedOS?.numeroOS === os.numeroOS ? 'selected' : ''} ${highlightedOS === os.numeroOS ? 'highlighted' : ''} ${canDragOficinaOs ? 'os-card--draggable-pendente' : ''}`}
                        onDragStart={(e) => handleOsOficinaDragStart(e, os.numeroOS)}
                        onDragEnd={handleOsOficinaDragEnd}
                        onClick={() => {
                          setHighlightedOS(null);
                          setSelectedOS({
                            numeroOS: os.numeroOS,
                            numeroPatrimonio: os.patrimonio,
                            numeroSerie: os.numeroSerie,
                            problema: os.defeito,
                            entrada: os.dataAbertura,
                            nomeCliente: nomeCliente || null
                          });
                        }}
                      >
                        <div className="os-card-header">
                          <div className="os-badge">{os.numeroOS}</div>
                          <div className="os-numero-principal">{os.patrimonio}</div>
                          <div
                            className={`os-data-principal${previsaoAtrasada ? ' os-data-principal--atrasada' : ''}`}
                          >
                            {os.calcPrevisaoFmt ?? os.calcPrevisao ?? os.dataAbertura}
                          </div>
                        </div>
                        <div className="os-card-body">
                          <div className="os-card-left">
                            <div className="os-details">
                              <div className="os-cliente-nome" title={nomeCliente || undefined}>
                                {nomeCliente || '—'}
                              </div>
                              <div className="os-serie-valor">{os.numeroSerie || '—'}</div>
                              <div className="os-problema-desc">{os.defeito}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>Nenhuma ordem de serviço encontrada</div>
              )}
            </div>
          </div>
        )}
          </div>

          {selectedEquipment && (
            <aside className="oficina-detail-rail" aria-label="Detalhes da ordem de serviço">
          {selectedOS ? (
            <div className={`os-detail-panel ${showClosureForm ? 'show-form' : ''}`}>
              {!showClosureForm ? (
                <>
                  <div className="os-detail-header">
                    <div className="os-detail-info">
                      <div className="os-detail-modelo-row">
                        <span className="os-badge" title="Número da OS">
                          {selectedOS.numeroOS}
                        </span>
                        <div className="os-detail-modelo">
                          {selectedOS.numeroSerie} | {selectedOS.numeroPatrimonio}
                        </div>
                      </div>
                      <div className="os-detail-field">
                        <label className="os-detail-label">Defeito</label>
                        <div className="os-detail-value">{osDetalhes.defeito || selectedOS.problema || '-'}</div>
                      </div>
                      <div className="os-detail-field os-detail-field--observacoes">
                        <div className="os-detail-observacoes-block">
                          <span className="os-detail-observacoes-label">Observações</span>
                          <textarea
                            className="os-detail-observacoes-laudo-text"
                            readOnly
                            rows={5}
                            value={
                              osDetalhes.observacoes && osDetalhes.observacoes !== '-'
                                ? osDetalhes.observacoes
                                : ''
                            }
                            placeholder="Nenhuma observação registrada."
                          />
                        </div>
                      </div>
                    </div>
                    <button 
                      className="os-detail-close"
                      onClick={() => setSelectedOS(null)}
                    >
                      ✕
                    </button>
                  </div>
                  <div className="os-detail-content">
                {/* Seção de Último Atendimento */}
                <div className="os-last-service-section">
                  <div className="os-last-service-title-row">
                    <h3 className="os-last-service-title">Último atendimento</h3>
                    <button
                      type="button"
                      className="os-contadores-open-btn"
                      onClick={openContadoresModal}
                      title="Ver últimos contadores"
                    >
                      <Gauge size={14} strokeWidth={2} aria-hidden />
                      Últimos contadores
                    </button>
                  </div>
                  {isLoadingUltimoAtendimento ? (
                    <div className="os-last-service-skeleton">
                      <div className="skeleton-row"></div>
                      <div className="skeleton-row"></div>
                      <div className="skeleton-row"></div>
                      <div className="skeleton-row skeleton-row-full"></div>
                    </div>
                  ) : ultimoAtendimento ? (
                    <div className="os-last-service-grid">
                      <div className="os-service-field">
                        <label className="os-service-label">OS</label>
                        <div className="os-service-value">{ultimoAtendimento.TB02115_CODIGO || '-'}</div>
                      </div>
                      <div className="os-service-field">
                        <label className="os-service-label">Data</label>
                        <div className="os-service-value">
                          {ultimoAtendimento.TB02115_DATA ? new Date(ultimoAtendimento.TB02115_DATA).toLocaleDateString('pt-BR') : '-'}
                        </div>
                      </div>
                      <div className="os-service-field">
                        <label className="os-service-label">Tipo de OS</label>
                        <div className="os-service-value">{ultimoAtendimento.TIPO_OS || '-'}</div>
                      </div>
                      <div className="os-service-field">
                        <label className="os-service-label">Motivo</label>
                        <div className="os-service-value">{ultimoAtendimento.TB02115_NOME || '-'}</div>
                      </div>
                      <div className="os-service-field">
                        <label className="os-service-label">Condição final</label>
                        <div className="os-service-value">{ultimoAtendimento.TB01055_NOME || '-'}</div>
                      </div>
                      <div className="os-service-field os-service-field-full">
                        <label className="os-service-label">Laudo</label>
                        <div className="os-service-value">{ultimoAtendimento.TB02122_OBS || '-'}</div>
                      </div>
                      <div className="os-service-field">
                        <label className="os-service-label">Técnico</label>
                        <div className="os-service-value">{ultimoAtendimento.TB01024_NOME || '-'}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="os-last-service-empty">
                      <div className="os-service-value">Nenhum atendimento encontrado</div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Footer com botões de ação */}
              <div className="os-detail-footer">
                <button 
                  className="os-history-button"
                  onClick={() => setShowHistoryModal(true)}
                >
                  <span className="button-icon">🖨</span>
                  Histórico
                </button>
                {selectedEquipment?.statusCard === 'EM ANDAMENTO' ? (
                  <>
                    <button 
                      className="os-action-button"
                      style={{ 
                        backgroundColor: '#fbbf24',
                        color: '#92400e'
                      }}
                      onClick={handlePauseReview}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <>
                          <span className="button-icon spinner">⟳</span>
                          Processando...
                        </>
                      ) : (
                        <>
                          <span className="button-icon">⏸</span>
                          Pausar revisão
                        </>
                      )}
                    </button>
                    <button 
                      className="os-action-button"
                      style={{ 
                        backgroundColor: getStatusBadge().cor,
                        color: getStatusBadge().textColor 
                      }}
                      onClick={handleActionButton}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <>
                          <span className="button-icon spinner">⟳</span>
                          Processando...
                        </>
                      ) : (
                        <>
                          <span className="button-icon">✓</span>
                          Finalizar revisão
                        </>
                      )}
                    </button>
                  </>
                ) : selectedEquipment?.statusCard === 'PAUSADO' ? (
                  <button 
                    className="os-action-button"
                    style={{ 
                      backgroundColor: '#dbeafe',
                      color: '#1e40af' 
                    }}
                    onClick={handleContinueReview}
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <>
                        <span className="button-icon spinner">⟳</span>
                        Processando...
                      </>
                    ) : (
                      <>
                        <span className="button-icon">▶</span>
                        Continuar revisão
                      </>
                    )}
                  </button>
                ) : (
                  <button 
                    className="os-action-button"
                    style={{ 
                      backgroundColor: getStatusBadge().cor,
                      color: getStatusBadge().textColor 
                    }}
                    onClick={handleActionButton}
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <>
                        <span className="button-icon spinner">⟳</span>
                        Processando...
                      </>
                    ) : (
                      <>
                        {getStatusBadge().texto === 'Finalizar revisão' && <span className="button-icon">✓</span>}
                        {getStatusBadge().texto === 'Iniciar revisão' && <span className="button-icon">▶</span>}
                        {getStatusBadge().texto === 'Em andamento' && <span className="button-icon">⟳</span>}
                        {getStatusBadge().texto}
                      </>
                    )}
                  </button>
                )}
              </div>
                </>
              ) : (
                <>
                  {/* Formulário de Fechamento */}
                  <div className="os-detail-header">
                    <div className="os-detail-info">
                      <div className="os-detail-modelo">Fechamento - OS {selectedOS.numeroOS}</div>
                    </div>
                    <button 
                      className="os-detail-close"
                      onClick={() => {
                        setShowClosureForm(false);
                        setSelectedOS(null);
                        setShowA3PB(false);
                        setShowA3Cor(false);
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  
                  <div className="os-detail-content">
                    <div className="closure-form">
                      {/* Data e Hora */}
                      <div className="form-row">
                        <div className="form-field">
                          <label className="form-label">Data do término</label>
                          <input 
                            type="date"
                            className="form-input"
                            max={new Date().toISOString().split('T')[0]}
                            value={closureForm.dataTermino}
                            onChange={(e) => {
                              const selectedDate = e.target.value;
                              const today = new Date().toISOString().split('T')[0];
                              if (selectedDate <= today) {
                                setClosureForm({...closureForm, dataTermino: selectedDate});
                              }
                            }}
                          />
                        </div>
                        <div className="form-field">
                          <label className="form-label">Hora do término</label>
                          <input 
                            type="time"
                            className="form-input"
                            step="60"
                            value={closureForm.horaTermino}
                            onChange={(e) => setClosureForm({...closureForm, horaTermino: e.target.value})}
                          />
                        </div>
                      </div>
                      
                      {/* Condição Final */}
                      <div className="form-field">
                        <label className="form-label">Condição final</label>
                        {!closureForm.condicaoFinal ? (
                          <div className="oficina-condicao-select" ref={condicaoSelectRef}>
                            <button
                              type="button"
                              className="oficina-condicao-select-trigger"
                              onClick={() => !isLoadingCondicoes && setCondicaoSelectOpen((o) => !o)}
                              disabled={isLoadingCondicoes}
                              aria-expanded={condicaoSelectOpen}
                              aria-haspopup="listbox"
                            >
                              <span className="oficina-condicao-select-trigger-label">
                                {isLoadingCondicoes ? 'Carregando…' : 'Selecione a condição final'}
                              </span>
                              <span
                                className="oficina-condicao-select-chevron"
                                aria-hidden
                                style={{ transform: condicaoSelectOpen ? 'rotate(180deg)' : 'none' }}
                              >
                                ▼
                              </span>
                            </button>
                            {condicaoSelectOpen && !isLoadingCondicoes && condicoesOptions.length > 0 && (
                              <div className="oficina-condicao-select-dropdown" role="listbox">
                                {condicoesOptions.map((condicao, idx) => (
                                  <div
                                    key={`${condicao.TB01055_CODIGO}-${idx}`}
                                    role="option"
                                    className="oficina-condicao-select-option"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      setClosureForm({
                                        ...closureForm,
                                        condicaoFinal: condicao.TB01055_NOME,
                                        codigoCondicao: condicao.TB01055_CODIGO
                                      });
                                      setCondicaoSelectOpen(false);
                                    }}
                                  >
                                    {condicao.TB01055_NOME}
                                  </div>
                                ))}
                              </div>
                            )}
                            {condicaoSelectOpen && !isLoadingCondicoes && condicoesOptions.length === 0 && (
                              <div className="oficina-condicao-select-dropdown oficina-condicao-select-empty" role="status">
                                Nenhuma condição disponível
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="selected-value">
                            {closureForm.condicaoFinal}
                            <button 
                              className="clear-button"
                              onClick={() => setClosureForm({...closureForm, condicaoFinal: '', codigoCondicao: ''})}
                            >
                              ✕
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Contadores */}
                      <div className="form-section">
                        <h3 className="form-section-title">Contadores</h3>
                        <div className={`counters-grid ${showA3PB || showA3Cor ? 'has-a3' : ''}`}>
                          <div className="form-field-small">
                            <label className="form-label-small">A4 P&B</label>
                            <input 
                              type="text"
                              className="form-input-small"
                              value={closureForm.contadores.a4pb}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                setClosureForm({
                                  ...closureForm, 
                                  contadores: {...closureForm.contadores, a4pb: value}
                                });
                              }}
                            />
                          </div>
                          <div className="form-field-small">
                            <label className="form-label-small">A4 Cor</label>
                            <input 
                              type="text"
                              className="form-input-small"
                              value={closureForm.contadores.a4cor}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                setClosureForm({
                                  ...closureForm, 
                                  contadores: {...closureForm.contadores, a4cor: value}
                                });
                              }}
                            />
                          </div>
                          <div className="form-field-small">
                            <label className="form-label-small">Dig.</label>
                            <input 
                              type="text"
                              className="form-input-small"
                              value={closureForm.contadores.dig}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                setClosureForm({
                                  ...closureForm, 
                                  contadores: {...closureForm.contadores, dig: value}
                                });
                              }}
                            />
                          </div>
                          {showA3PB && (
                            <div className="form-field-small">
                              <label className="form-label-small">A3 P&B</label>
                              <input 
                                type="text"
                                className="form-input-small"
                                value={closureForm.contadores.a3pb}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '');
                                  setClosureForm({
                                    ...closureForm, 
                                    contadores: {...closureForm.contadores, a3pb: value}
                                  });
                                }}
                              />
                            </div>
                          )}
                          {showA3Cor && (
                            <div className="form-field-small">
                              <label className="form-label-small">A3 Cor</label>
                              <input 
                                type="text"
                                className="form-input-small"
                                value={closureForm.contadores.a3cor}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '');
                                  setClosureForm({
                                    ...closureForm, 
                                    contadores: {...closureForm.contadores, a3cor: value}
                                  });
                                }}
                              />
                            </div>
                          )}
                        </div>
                        <div className="add-counter-buttons">
                          {!showA3PB && (
                            <button 
                              type="button"
                              className="add-counter-btn"
                              onClick={() => setShowA3PB(true)}
                            >
                              + A3 P&B
                            </button>
                          )}
                          {!showA3Cor && (
                            <button 
                              type="button"
                              className="add-counter-btn"
                              onClick={() => setShowA3Cor(true)}
                            >
                              + A3 Cor
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Laudo Técnico */}
                      <div className="form-field form-field--closure-laudo">
                        <label className="form-label">Laudo técnico</label>
                        <textarea 
                          className="form-textarea form-textarea--closure-laudo"
                          rows="4"
                          placeholder="Descreva os procedimentos realizados..."
                          value={closureForm.laudoTecnico}
                          onChange={(e) => setClosureForm({...closureForm, laudoTecnico: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Footer com botão Concluir */}
                  <div className="os-detail-footer">
                    <button 
                      className="os-action-button"
                      style={{ 
                        backgroundColor: '#10b981',
                        color: 'white',
                        opacity: (!closureForm.dataTermino || !closureForm.horaTermino || !closureForm.codigoCondicao || !closureForm.laudoTecnico) ? 0.5 : 1,
                        cursor: (!closureForm.dataTermino || !closureForm.horaTermino || !closureForm.codigoCondicao || !closureForm.laudoTecnico) ? 'not-allowed' : 'pointer'
                      }}
                      onClick={handleConcluirOS}
                      disabled={isProcessing || !closureForm.dataTermino || !closureForm.horaTermino || !closureForm.codigoCondicao || !closureForm.laudoTecnico}
                    >
                      {isProcessing ? (
                        <>
                          <span className="button-icon spinner">⟳</span>
                          Processando...
                        </>
                      ) : (
                        <>
                          <span className="button-icon">✓</span>
                          Concluir
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="oficina-detail-rail-placeholder">
              Selecione uma ordem de serviço na lista para ver os detalhes.
            </div>
          )}
            </aside>
          )}
        </div>
      </div>
      
      {/* Notificação */}
      {notification && (
        <div className="notification-container">
          <div
            className={`notification ${notification.type} ${notification.moveToast ? 'notification--move-toast' : ''}`}
          >
            <span
              className={`notification-icon ${notification.type === 'error' ? 'notification-icon--error' : ''}`}
            >
              {notification.type === 'error' ? '!' : '✓'}
            </span>
            <span className="notification-message">{notification.message}</span>
          </div>
        </div>
      )}

      {showAlterarStatusModal && (
        <div className="oficina-alterar-status-backdrop" role="dialog" aria-modal="true" aria-labelledby="oficina-alterar-status-title">
          <div className="oficina-alterar-status-modal">
            <div className="oficina-alterar-status-header">
              <h2 id="oficina-alterar-status-title" className="oficina-alterar-status-title">Altere o status</h2>
            </div>
            <div className="oficina-alterar-status-body">
              {isOpcoesStatusLoading ? (
                <div className="oficina-alterar-status-loading">Carregando opções...</div>
              ) : (
                <label className="oficina-alterar-status-label">
                  <span className="oficina-alterar-status-label-text">Novo status</span>
                  <AlterarStatusSelectOficina
                    value={selectedCodStatusConclusao}
                    onChange={setSelectedCodStatusConclusao}
                    options={opcoesStatusConclusao}
                    disabled={alterarStatusLoading}
                  />
                </label>
              )}
            </div>
            <div className="oficina-alterar-status-footer">
              {alterarStatusFeedback === 'success' && (
                <div className="oficina-alterar-status-feedback success" role="status">
                  <CheckCircle size={18} strokeWidth={2} />
                  <span>Status alterado com sucesso</span>
                </div>
              )}
              {alterarStatusFeedback === 'error' && (
                <div className="oficina-alterar-status-feedback error" role="alert">
                  <X size={18} strokeWidth={2} />
                  <span>Não foi possível alterar. Tente novamente.</span>
                </div>
              )}
              <div className="oficina-alterar-status-actions">
                <button type="button" className="oficina-alterar-status-btn-confirm" onClick={handleConfirmarAlterarStatus} disabled={alterarStatusLoading || !selectedCodStatusConclusao} aria-busy={alterarStatusLoading}>
                  {alterarStatusLoading ? <span className="oficina-alterar-status-spinner" aria-hidden /> : alterarStatusFeedback === 'success' ? <CheckCircle size={18} strokeWidth={2} /> : null}
                  <span>{alterarStatusLoading ? 'Processando...' : alterarStatusFeedback === 'success' ? 'Concluído' : 'Confirmar'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showContadoresModal && selectedOS && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="os-contadores-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowContadoresModal(false);
          }}
        >
          <div className="os-contadores-modal" onClick={(e) => e.stopPropagation()}>
            <div className="os-contadores-modal-header">
              <h2 id="os-contadores-modal-title" className="os-contadores-modal-title">
                <Gauge size={18} strokeWidth={2} aria-hidden />
                Últimos contadores
              </h2>
              <button
                type="button"
                className="os-contadores-modal-close"
                onClick={() => setShowContadoresModal(false)}
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>
            <div className="os-contadores-modal-body">
              <p className="os-contadores-modal-serie">
                Série: <strong>{selectedOS.numeroSerie || '—'}</strong>
              </p>
              {isLoadingContadores ? (
                <div className="os-counters-skeleton">
                  <div className="skeleton-row" />
                  <div className="skeleton-row" />
                </div>
              ) : (
                <table className="os-counters-table">
                  <thead>
                    <tr>
                      <th>A4 P&B</th>
                      <th>A4 Cor</th>
                      <th>Dig.</th>
                      <th>A3 P&B</th>
                      <th>A3 Cor</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{(contadores.a4pb || 0).toLocaleString('pt-BR')}</td>
                      <td>{(contadores.a4cor || 0).toLocaleString('pt-BR')}</td>
                      <td>{(contadores.dig || 0).toLocaleString('pt-BR')}</td>
                      <td>{(contadores.a3pb || 0).toLocaleString('pt-BR')}</td>
                      <td>{(contadores.a3cor || 0).toLocaleString('pt-BR')}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Histórico */}
      {showHistoryModal && selectedOS && (
        <div className="modal-overlay">
          <div className={`modal-history ${activeHistoryTab === 'ordens' ? 'modal-history-wide' : ''}`}>
            {/* Header do Modal */}
            <div className="modal-history-header">
              <div className="modal-header-info">
                <h3 className="modal-title">{selectedEquipment.modelo}</h3>
                <div className="modal-subtitle">
                  <span>Série: {selectedOS.numeroSerie}</span>
                  <span>•</span>
                  <span>Patrimônio: {selectedOS.numeroPatrimonio}</span>
                </div>
              </div>
              <button 
                className="modal-close-button"
                onClick={() => setShowHistoryModal(false)}
              >
                ✕
              </button>
            </div>
            
            {/* Abas */}
            <div className="modal-tabs">
              <button 
                className={`modal-tab ${activeHistoryTab === 'movimentacao' ? 'active' : ''}`}
                onClick={() => setActiveHistoryTab('movimentacao')}
              >
                Movimentação
              </button>
              <button 
                className={`modal-tab ${activeHistoryTab === 'ordens' ? 'active' : ''}`}
                onClick={() => setActiveHistoryTab('ordens')}
              >
                Ordens de serviço
              </button>
              <button 
                className={`modal-tab ${activeHistoryTab === 'pecas' ? 'active' : ''}`}
                onClick={() => setActiveHistoryTab('pecas')}
              >
                Peças trocadas
              </button>
            </div>
            
            {/* Conteúdo */}
            <div className="modal-content">
              {activeHistoryTab === 'movimentacao' && (
                <div className="movimentacao-content">
                  {isLoadingMovimentacao ? (
                    <div className="movimentacao-skeleton">
                      <div className="skeleton-row skeleton-timeline"></div>
                      <div className="skeleton-row skeleton-table"></div>
                    </div>
                  ) : (
                    <>
                      {/* Stepper */}
                      <div className="timeline-stepper">
                        {[...movimentacaoData].sort((a, b) => {
                          const dateA = new Date(a.TB02112_DATA);
                          const dateB = new Date(b.TB02112_DATA);
                          return dateA - dateB; // Mais antiga primeiro (esquerda para direita)
                        }).map((mov, index, sorted) => (
                          <div key={index} className="step-item">
                            <div className="step-marker">
                              <div className="step-dot"></div>
                              {index < sorted.length - 1 && <div className="step-line"></div>}
                            </div>
                            <div className="step-content">
                              <div className="step-title">{mov.TB01008_NOME || '-'}</div>
                              <div className="step-subtitle">{mov.TB02112_CODIGO || '-'}</div>
                              <div className="step-dates">
                                <span className="step-date-label">Instalação:</span>
                                <span className="step-date-value">
                                  {mov.TB02112_DATA ? new Date(mov.TB02112_DATA).toLocaleDateString('pt-BR') : '-'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Tabela */}
                      <div className="movimentacao-table-wrapper">
                        <table className="movimentacao-table">
                          <thead>
                            <tr>
                              <th>Cliente</th>
                              <th>Contrato</th>
                              <th>Instalação</th>
                              <th>OS Corretivas</th>
                              <th>OS Preventivas</th>
                              <th>Desinstalação</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...movimentacaoData].sort((a, b) => {
                              const dateA = new Date(a.TB02112_DATA);
                              const dateB = new Date(b.TB02112_DATA);
                              return dateB - dateA; // Mais recente primeiro (topo)
                            }).map((mov, index) => (
                              <tr key={index}>
                                <td>{mov.TB01008_NOME || '-'}</td>
                                <td>{mov.TB02112_CODIGO || '-'}</td>
                                <td>{mov.TB02112_DATA ? new Date(mov.TB02112_DATA).toLocaleDateString('pt-BR') : '-'}</td>
                                <td className="td-center">{mov.OS_CORRETIVAS || 0}</td>
                                <td className="td-center">{mov.OS_PREVENTIVAS || 0}</td>
                                <td>{mov.TB02112_DTINATIVO ? new Date(mov.TB02112_DTINATIVO).toLocaleDateString('pt-BR') : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {activeHistoryTab === 'ordens' && (
                <div className="ordens-content">
                  {isLoadingOrdensHistorico ? (
                    <div className="ordens-table-wrapper">
                      <table className="ordens-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                          <tr>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...Array(8)].map((_, idx) => (
                            <tr key={idx}>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <>
                      <div className="ordens-table-wrapper">
                        <table className="ordens-table">
                          <thead>
                            <tr>
                              <th>OS</th>
                              <th>
                                <div className="th-with-filter">
                                  Tipo
                                  <button 
                                    className="filter-icon-button"
                                    onClick={() => setShowTipoFilter(!showTipoFilter)}
                                    title="Filtrar tipos"
                                  >
                                    <ListFilter size={14} strokeWidth={2} />
                                  </button>
                                </div>
                                {showTipoFilter && (
                                  <div className="multiselect-filter-popup">
                                    <input 
                                      type="text"
                                      className="filter-search-input"
                                      placeholder="Buscar tipo..."
                                      value={tipoSearchText}
                                      onChange={(e) => setTipoSearchText(e.target.value)}
                                    />
                                    <div className="filter-actions">
                                      <button 
                                        className="filter-action-button"
                                        onClick={() => setSelectedTipos(uniqueTipos)}
                                      >
                                        Marcar todos
                                      </button>
                                      <button 
                                        className="filter-action-button"
                                        onClick={() => setSelectedTipos([])}
                                      >
                                        Limpar
                                      </button>
                                    </div>
                                    <div className="filter-options-list">
                                      {filteredTiposList.map((tipo, idx) => (
                                        <label key={idx} className="filter-option">
                                          <input 
                                            type="checkbox"
                                            checked={selectedTipos.includes(tipo)}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setSelectedTipos([...selectedTipos, tipo]);
                                              } else {
                                                setSelectedTipos(selectedTipos.filter(t => t !== tipo));
                                              }
                                            }}
                                          />
                                          <span>{tipo}</span>
                                        </label>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </th>
                          <th>
                            <div className="th-with-filter">
                              Abertura
                              <button 
                                className="filter-icon-button"
                                onClick={() => setShowAberturaFilter(!showAberturaFilter)}
                                title="Filtrar por período"
                              >
                                <Calendar1 size={14} strokeWidth={2} />
                              </button>
                            </div>
                            {showAberturaFilter && (
                              <div className="date-filter-popup">
                                <input 
                                  type="date"
                                  className="date-filter-input"
                                  value={aberturaStart}
                                  onChange={(e) => setAberturaStart(e.target.value)}
                                  placeholder="Data início"
                                />
                                <input 
                                  type="date"
                                  className="date-filter-input"
                                  value={aberturaEnd}
                                  onChange={(e) => setAberturaEnd(e.target.value)}
                                  placeholder="Data fim"
                                />
                                <button 
                                  className="clear-filter-button"
                                  onClick={() => {
                                    setAberturaStart('');
                                    setAberturaEnd('');
                                  }}
                                >
                                  Limpar
                                </button>
                              </div>
                            )}
                          </th>
                          <th>
                            <div className="th-with-filter">
                              Cliente
                              <button 
                                className="filter-icon-button"
                                onClick={() => setShowClienteFilter(!showClienteFilter)}
                                title="Filtrar clientes"
                              >
                                <ListFilter size={14} strokeWidth={2} />
                              </button>
                            </div>
                            {showClienteFilter && (
                              <div className="multiselect-filter-popup">
                                <input 
                                  type="text"
                                  className="filter-search-input"
                                  placeholder="Buscar cliente..."
                                  value={clienteSearchText}
                                  onChange={(e) => setClienteSearchText(e.target.value)}
                                />
                                <div className="filter-actions">
                                  <button 
                                    className="filter-action-button"
                                    onClick={() => setSelectedClientes(uniqueClientes)}
                                  >
                                    Marcar todos
                                  </button>
                                  <button 
                                    className="filter-action-button"
                                    onClick={() => setSelectedClientes([])}
                                  >
                                    Limpar
                                  </button>
                                </div>
                                <div className="filter-options-list">
                                  {filteredClientesList.map((cliente, idx) => (
                                    <label key={idx} className="filter-option">
                                      <input 
                                        type="checkbox"
                                        checked={selectedClientes.includes(cliente)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedClientes([...selectedClientes, cliente]);
                                          } else {
                                            setSelectedClientes(selectedClientes.filter(c => c !== cliente));
                                          }
                                        }}
                                      />
                                      <span>{cliente}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            )}
                          </th>
                          <th>Solicitante</th>
                          <th>Motivo</th>
                          <th>
                            <div className="th-with-filter">
                              Técnico
                              <button 
                                className="filter-icon-button"
                                onClick={() => setShowTecnicoFilter(!showTecnicoFilter)}
                                title="Filtrar técnicos"
                              >
                                <ListFilter size={14} strokeWidth={2} />
                              </button>
                            </div>
                            {showTecnicoFilter && (
                              <div className="multiselect-filter-popup">
                                <input 
                                  type="text"
                                  className="filter-search-input"
                                  placeholder="Buscar técnico..."
                                  value={tecnicoSearchText}
                                  onChange={(e) => setTecnicoSearchText(e.target.value)}
                                />
                                <div className="filter-actions">
                                  <button 
                                    className="filter-action-button"
                                    onClick={() => setSelectedTecnicos(uniqueTecnicos)}
                                  >
                                    Marcar todos
                                  </button>
                                  <button 
                                    className="filter-action-button"
                                    onClick={() => setSelectedTecnicos([])}
                                  >
                                    Limpar
                                  </button>
                                </div>
                                <div className="filter-options-list">
                                  {filteredTecnicosList.map((tecnico, idx) => (
                                    <label key={idx} className="filter-option">
                                      <input 
                                        type="checkbox"
                                        checked={selectedTecnicos.includes(tecnico)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedTecnicos([...selectedTecnicos, tecnico]);
                                          } else {
                                            setSelectedTecnicos(selectedTecnicos.filter(t => t !== tecnico));
                                          }
                                        }}
                                      />
                                      <span>{tecnico}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            )}
                          </th>
                          <th>Condição Final</th>
                          <th>
                            <div className="th-with-filter">
                              Fechamento
                              <button 
                                className="filter-icon-button"
                                onClick={() => setShowFechamentoFilter(!showFechamentoFilter)}
                                title="Filtrar por período"
                              >
                                <Calendar1 size={14} strokeWidth={2} />
                              </button>
                            </div>
                            {showFechamentoFilter && (
                              <div className="date-filter-popup">
                                <input 
                                  type="date"
                                  className="date-filter-input"
                                  value={fechamentoStart}
                                  onChange={(e) => setFechamentoStart(e.target.value)}
                                  placeholder="Data início"
                                />
                                <input 
                                  type="date"
                                  className="date-filter-input"
                                  value={fechamentoEnd}
                                  onChange={(e) => setFechamentoEnd(e.target.value)}
                                  placeholder="Data fim"
                                />
                                <button 
                                  className="clear-filter-button"
                                  onClick={() => {
                                    setFechamentoStart('');
                                    setFechamentoEnd('');
                                  }}
                                >
                                  Limpar
                                </button>
                              </div>
                            )}
                          </th>
                          <th>Laudo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrdens.length > 0 ? (
                          filteredOrdens.map((ordem, index) => (
                            <React.Fragment key={index}>
                              <tr>
                                <td className="td-bold">{ordem.TB02115_CODIGO || '-'}</td>
                                <td className="td-center">
                                  <span className="tipo-badge">{ordem.TIPO_OS || '-'}</span>
                                </td>
                                <td>{ordem.TB02115_DATA || '-'}</td>
                                <td>{ordem.TB01008_NOME || '-'}</td>
                                <td>{ordem.TB02115_SOLICITANTE || '-'}</td>
                                <td className="td-center">
                                  <button 
                                    className="icon-button"
                                    onClick={() => setExpandedMotivo(expandedMotivo === index ? null : index)}
                                    title="Ver detalhes do motivo"
                                  >
                                    ℹ️
                                  </button>
                                </td>
                                <td>{ordem.TB01024_NOME || '-'}</td>
                                <td>
                                  <span className="tipo-badge">{ordem.TB01055_NOME || '-'}</span>
                                </td>
                                <td>{ordem.TB02122_DATAFIM || '-'}</td>
                                <td className="td-center">
                                  <button 
                                    className="icon-button"
                                    onClick={() => setExpandedLaudo(expandedLaudo === index ? null : index)}
                                    title="Ver laudo técnico"
                                  >
                                    📄
                                  </button>
                                </td>
                              </tr>
                              {expandedMotivo === index && (
                                <tr className="expanded-row">
                                  <td colSpan="10">
                                    <div className="expanded-content">
                                      <strong>Motivo:</strong> {ordem.TB02115_NOME || '-'}
                                    </div>
                                  </td>
                                </tr>
                              )}
                              {expandedLaudo === index && (
                                <tr className="expanded-row">
                                  <td colSpan="10">
                                    <div className="expanded-content">
                                      <strong>Laudo Técnico:</strong> {ordem.TB02122_OBS || '-'}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="10" style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
                              Nenhuma ordem de serviço encontrada
                            </td>
                          </tr>
                        )}
                          </tbody>
                        </table>
                      </div>
                      <div className="table-summary">
                        <span className="summary-label">Total de registros:</span>
                        <span className="summary-value">{filteredOrdens.length}</span>
                        <div className="export-button-wrapper" ref={exportMenuRef}>
                          <button 
                            className="export-button"
                            onClick={() => setShowExportMenu(!showExportMenu)}
                            title="Exportar dados"
                          >
                            <Download size={14} strokeWidth={2} />
                            Exportar dados
                          </button>
                          {showExportMenu && (
                            <div className="export-menu">
                              <button 
                                className="export-menu-item"
                                onClick={exportToPDF}
                              >
                                <FileText size={16} strokeWidth={2} />
                                PDF
                              </button>
                              <button 
                                className="export-menu-item"
                                onClick={exportToExcel}
                              >
                                <FileSpreadsheet size={16} strokeWidth={2} />
                                Excel
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {activeHistoryTab === 'pecas' && (
                <div className="pecas-content">
                  {isLoadingPecasTrocadas ? (
                    <div className="pecas-table-wrapper">
                      <table className="pecas-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                          <tr>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                            <th style={{ height: '40px' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...Array(8)].map((_, idx) => (
                            <tr key={idx}>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                              <td><div className="skeleton-row" style={{ height: '40px' }}></div></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <>
                      <div className="pecas-table-wrapper">
                        <table className="pecas-table">
                          <thead>
                            <tr>
                              <th>Pedido</th>
                              <th>
                                <div className="th-with-filter">
                                  Data
                                  <button 
                                    className="filter-icon-button"
                                    onClick={() => setShowPecasDataFilter(!showPecasDataFilter)}
                                    title="Filtrar por período"
                                  >
                                    <Calendar1 size={14} strokeWidth={2} />
                                  </button>
                                </div>
                                {showPecasDataFilter && (
                                  <div className="date-filter-popup">
                                    <input 
                                      type="date"
                                      className="date-filter-input"
                                      value={pecasDataStart}
                                      onChange={(e) => setPecasDataStart(e.target.value)}
                                    />
                                    <input 
                                      type="date"
                                      className="date-filter-input"
                                      value={pecasDataEnd}
                                      onChange={(e) => setPecasDataEnd(e.target.value)}
                                    />
                                    <button 
                                      className="clear-filter-button"
                                      onClick={() => {
                                        setPecasDataStart('');
                                        setPecasDataEnd('');
                                      }}
                                    >
                                      Limpar
                                    </button>
                                  </div>
                                )}
                              </th>
                              <th>Código</th>
                              <th>
                                <div className="th-with-filter">
                                  Referência
                                  <button 
                                    className="filter-icon-button"
                                    onClick={() => setShowReferenciaFilter(!showReferenciaFilter)}
                                    title="Filtrar referências"
                                  >
                                    <ListFilter size={14} strokeWidth={2} />
                                  </button>
                                </div>
                                {showReferenciaFilter && (
                                  <div className="multiselect-filter-popup">
                                    <input 
                                      type="text"
                                      className="filter-search-input"
                                      placeholder="Buscar referência..."
                                      value={referenciaSearchText}
                                      onChange={(e) => setReferenciaSearchText(e.target.value)}
                                    />
                                    <div className="filter-actions">
                                      <button 
                                        className="filter-action-button"
                                        onClick={() => setSelectedReferencias(uniqueReferencias)}
                                      >
                                        Marcar todos
                                      </button>
                                      <button 
                                        className="filter-action-button"
                                        onClick={() => setSelectedReferencias([])}
                                      >
                                        Limpar
                                      </button>
                                    </div>
                                    <div className="filter-options-list">
                                      {filteredReferenciasList.map((ref, idx) => (
                                        <label key={idx} className="filter-option">
                                          <input 
                                            type="checkbox"
                                            checked={selectedReferencias.includes(ref)}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setSelectedReferencias([...selectedReferencias, ref]);
                                              } else {
                                                setSelectedReferencias(selectedReferencias.filter(r => r !== ref));
                                              }
                                            }}
                                          />
                                          <span>{ref}</span>
                                        </label>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </th>
                              <th>
                                <div className="th-with-filter">
                                  Produto
                                  <button 
                                    className="filter-icon-button"
                                    onClick={() => setShowProdutoFilter(!showProdutoFilter)}
                                    title="Filtrar produtos"
                                  >
                                    <ListFilter size={14} strokeWidth={2} />
                                  </button>
                                </div>
                                {showProdutoFilter && (
                                  <div className="multiselect-filter-popup">
                                    <input 
                                      type="text"
                                      className="filter-search-input"
                                      placeholder="Buscar produto..."
                                      value={produtoSearchText}
                                      onChange={(e) => setProdutoSearchText(e.target.value)}
                                    />
                                    <div className="filter-actions">
                                      <button 
                                        className="filter-action-button"
                                        onClick={() => setSelectedProdutos(uniqueProdutos)}
                                      >
                                        Marcar todos
                                      </button>
                                      <button 
                                        className="filter-action-button"
                                        onClick={() => setSelectedProdutos([])}
                                      >
                                        Limpar
                                      </button>
                                    </div>
                                    <div className="filter-options-list">
                                      {filteredProdutosList.map((prod, idx) => (
                                        <label key={idx} className="filter-option">
                                          <input 
                                            type="checkbox"
                                            checked={selectedProdutos.includes(prod)}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setSelectedProdutos([...selectedProdutos, prod]);
                                              } else {
                                                setSelectedProdutos(selectedProdutos.filter(p => p !== prod));
                                              }
                                            }}
                                          />
                                          <span>{prod}</span>
                                        </label>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </th>
                              <th>Qtde</th>
                              <th>OS</th>
                              <th>Vida útil</th>
                              <th>P&amp;B</th>
                              <th>Cor</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredPecas.length > 0 ? (
                              filteredPecas.map((peca, index) => (
                                <tr key={index}>
                                  <td className="td-bold">{peca.TB02022_CODIGO || '-'}</td>
                                  <td>{peca.TB02021_DATA || '-'}</td>
                                  <td>{peca.TB02022_PRODUTO || '-'}</td>
                                  <td>{peca.TB01010_REFERENCIA || '-'}</td>
                                  <td>{peca.TB01010_NOME || '-'}</td>
                                  <td className="td-center">{parseInt(peca.TB02022_QTPROD || 0)}</td>
                                  <td className="td-bold">{peca.TB02021_OS || '-'}</td>
                                  <td className="td-right">{parseInt(peca.TB01010_COPIA || 0).toLocaleString('pt-BR')}</td>
                                  <td className="td-right">{formatPecaContador(peca.TB02021_CONTPB)}</td>
                                  <td className="td-right">{formatPecaContador(peca.TB02021_CONTCOLOR)}</td>
                                  <td className="td-right">{formatPecaContador(peca.TB02021_CONTTOTAL)}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="11" style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
                                  Nenhuma peça trocada encontrada
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <div className="table-summary">
                        <span className="summary-label">Total de registros:</span>
                        <span className="summary-value">{filteredPecas.length}</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OficinaView;
